import React, { useEffect, useContext, useState, useCallback, useMemo, memo, useRef } from "react";
import { Container, Row, Col, Card, Button, Form, Table, Spinner, Nav, Modal } from "react-bootstrap";
import { Calendar } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";

// Contextos
import reservasContext from "../../context/reservas/reservasContext";
import activoContext from "../../context/activoContex/activoContex";
import authContext from "../../context/authentication/authContex";
import { SocketContext } from "../../context/socket/socket";
import bloqueosContext from "../../context/bloqueos/bloqueosContext";

// Componentes
import ModalNuevaReserva from "../../components/Reservas/ModalNuevaReserva";
import ModalEditarReserva from "../../components/Reservas/ModalEditarReserva";
import GestorBloqueosButton from "../../components/Reservas/GestorBloqueosButton";
import MisReservas from "../misReservas/MisReservas";
import MapaCanchas from "../../components/MapaCanchas/MapaCanchas";
import MapaCanchasPadel from "../../components/MapaCanchas/MapaCanchasPadel";

// Fecha
import moment from "moment";
import "moment/locale/es";

// Estilos
import "./ReservasCanchas.css";

// Cliente HTTP
import clienteAxios from "../../config/axios";
import { aplicarPropuestaOptimizacion as aplicarPropuestaOptimizacionAPI } from "../../api/reservas";

const TIPO_BLOQUEO_LABEL = {
  MANTENIMIENTO: "Mantenimiento",
  EVENTO_PRIVADO: "Evento Privado",
  CLIMA: "Clima",
  CLASES: "Clases",
  OTRO: "Otro",
};

// Misma tabla de duraciones que ModalNuevaReserva, para que el rango que se
// muestra en el modal de inicios coincida con la reserva que se va a crear.
const calcularHoraFinInicio = (horaInicio, tipoJuego) => {
  const [horas, minutos] = horaInicio.split(":").map(Number);
  let duracion = 60; // SINGLES y DOBLES_60
  if (tipoJuego === "SINGLES_45") duracion = 45;
  if (tipoJuego === "DOBLES") duracion = 75;
  if (tipoJuego === "SINGLES_PADEL" || tipoJuego === "DOBLES_PADEL") duracion = 90;

  const totalMinutos = horas * 60 + minutos + duracion;
  return `${String(Math.floor(totalMinutos / 60)).padStart(2, "0")}:${String(totalMinutos % 60).padStart(2, "0")}`;
};

// Calcula una propuesta de optimización que cierra huecos de hasta maxSlots (1 o 2)
// moviendo reservas y bloqueos de tipo MANTENIMIENTO hacia atrás. Los bloqueos que no
// son MANTENIMIENTO son anclas: no se mueven y frenan la compresión de los siguientes.
// Cada bloque se mueve como máximo maxSlots slots (15 o 30 min).
// Devuelve un array nuevo de slots del mismo largo que slotsCancha.
const calcularPropuestaOptimizacion = (slotsCancha, maxSlots = 2) => {
  if (!slotsCancha || slotsCancha.length === 0) return [];

  const bloques = [];
  let i = 0;
  while (i < slotsCancha.length) {
    const s = slotsCancha[i];
    if (s.estado === "DISPONIBLE") { i++; continue; }
    const grupoID = s.reservaID || s.bloqueoID || `idx-${i}`;
    const inicio = i;
    let j = i;
    while (j < slotsCancha.length) {
      const t = slotsCancha[j];
      const tGrupo = t.reservaID || t.bloqueoID || `idx-${j}`;
      if (t.estado === s.estado && tGrupo === grupoID) j++;
      else break;
    }
    bloques.push({
      estadoBase: s.estado,
      tipoBloqueo: s.tipo,
      esMultiDia: !!s.esMultiDia,
      indiceInicio: inicio,
      duracionSlots: j - inicio,
      slotsOriginales: slotsCancha.slice(inicio, j),
    });
    i = j;
  }

  // FASE 1: mover hacia ATRÁS para cerrar huecos de hasta maxSlots
  let prevEndIdx = 0;
  const movimientos = bloques.map((b) => {
    const esAncla = b.estadoBase === "BLOQUEADO" && (b.tipoBloqueo !== "MANTENIMIENTO" || b.esMultiDia);
    if (esAncla) {
      prevEndIdx = b.indiceInicio + b.duracionSlots;
      return { ...b, esAncla: true, nuevoInicio: b.indiceInicio, deltaSlots: 0 };
    }
    const gap = b.indiceInicio - prevEndIdx;
    if (gap >= 1 && gap <= maxSlots) {
      const nuevoInicio = prevEndIdx;
      prevEndIdx = nuevoInicio + b.duracionSlots;
      return { ...b, esAncla: false, nuevoInicio, deltaSlots: gap };
    }
    prevEndIdx = b.indiceInicio + b.duracionSlots;
    return { ...b, esAncla: false, nuevoInicio: b.indiceInicio, deltaSlots: 0 };
  });

  // FASE 2: para huecos remanentes de 1-2 slots (inutilizables), intentar
  // mover hacia ADELANTE para convertir el hueco en ≥ 3 slots (utilizable).
  // Cuando el bloque con hueco está pegado a los que siguen (sin huecos entre ellos),
  // se forma una CADENA contigua y la cadena entera se desplaza junta.
  // No se aplica si crea un nuevo hueco inutilizable a la derecha de la cadena.
  let k = 0;
  while (k < movimientos.length) {
    const m = movimientos[k];
    if (m.esAncla) { k++; continue; }

    const prevEnd = k === 0 ? 0 : movimientos[k - 1].nuevoInicio + movimientos[k - 1].duracionSlots;
    const leftGap = m.nuevoInicio - prevEnd;
    if (leftGap === 0 || leftGap >= 3) { k++; continue; }

    // Construir la cadena de bloques contiguos movibles desde k
    let chainEnd = k;
    while (chainEnd + 1 < movimientos.length) {
      const cur = movimientos[chainEnd];
      const nxt = movimientos[chainEnd + 1];
      const curEnd = cur.nuevoInicio + cur.duracionSlots;
      if (nxt.nuevoInicio === curEnd && !nxt.esAncla) {
        chainEnd++;
      } else {
        break;
      }
    }

    const needed = 3 - leftGap; // 1 o 2 slots

    // Cada bloque en la cadena tiene su propio tope de movimiento hacia adelante
    // según cuánto lo movió fase 1: maxShift = phase1Backward + maxSlots
    let chainMaxShift = Infinity;
    for (let i = k; i <= chainEnd; i++) {
      const b = movimientos[i];
      const p1Back = b.indiceInicio - b.nuevoInicio;
      chainMaxShift = Math.min(chainMaxShift, p1Back + maxSlots);
    }

    const lastInChain = movimientos[chainEnd];
    const chainEndPos = lastInChain.nuevoInicio + lastInChain.duracionSlots;
    const nextStart = chainEnd + 1 < movimientos.length
      ? movimientos[chainEnd + 1].nuevoInicio
      : slotsCancha.length;
    const spaceAfterChain = nextStart - chainEndPos;

    if (needed > chainMaxShift || needed > spaceAfterChain) {
      k = chainEnd + 1;
      continue;
    }
    const newRightGap = spaceAfterChain - needed;
    if (newRightGap !== 0 && newRightGap < 3) {
      k = chainEnd + 1;
      continue;
    }

    // Aplicar el desplazamiento adelante a toda la cadena
    for (let i = k; i <= chainEnd; i++) {
      movimientos[i].nuevoInicio += needed;
      movimientos[i].deltaSlots = movimientos[i].indiceInicio - movimientos[i].nuevoInicio;
    }

    k = chainEnd + 1;
  }

  const slotsPropuesta = slotsCancha.map((s) => ({
    horaInicio: s.horaInicio,
    horaFin: s.horaFin,
    estado: "DISPONIBLE",
  }));

  movimientos.forEach((mov) => {
    for (let k = 0; k < mov.duracionSlots; k++) {
      const srcSlot = mov.slotsOriginales[k];
      const destIdx = mov.nuevoInicio + k;
      if (destIdx >= slotsPropuesta.length) break;
      slotsPropuesta[destIdx] = {
        ...srcSlot,
        horaInicio: slotsPropuesta[destIdx].horaInicio,
        horaFin: slotsPropuesta[destIdx].horaFin,
        _movido: mov.deltaSlots !== 0,
        _deltaSlots: mov.deltaSlots, // signed: + atrás, − adelante
        _horaOriginal: srcSlot.horaInicio,
      };
    }
  });

  return slotsPropuesta;
};

// Componente memoizado para renderizar una celda individual
const SlotCell = memo(({
  cancha,
  horario,
  slot,
  hoverSlot,
  setHoverSlot,
  handleClickSlot,
  esPropuesta = false
}) => {
  // Usar el color pre-calculado que viene en slot._color
  const color = slot?._color || "#f8f9fa";

  // Determinar si es clickable basándose en el color (verde oscuro = puede iniciar)
  const isClickable = !esPropuesta && slot && slot.estado === "DISPONIBLE" && color === "#28a745";
  const isHovered = hoverSlot.horario === horario && hoverSlot.canchaID === cancha._id;

  const isSuspendida = slot && slot.estado === "SUSPENDIDA";
  const esMovido = esPropuesta && slot && slot._movido;
  const tooltipPropuesta = esMovido
    ? `Propuesto: ${slot._horaOriginal}hs → ${slot.horaInicio}hs (${slot._deltaSlots > 0 ? "−" : "+"}${Math.abs(slot._deltaSlots) * 15} min)`
    : undefined;

  return (
    <td
      style={{
        backgroundColor: color,
        cursor: isClickable ? "pointer" : "default",
        textAlign: "center",
        verticalAlign: "middle",
        transition: "transform 0.2s, opacity 0.2s",
        opacity: esPropuesta ? 0.85 : (color === "#28a745" ? 1 : 0.8),
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",
        WebkitTransform: "translateZ(0)",
        transform: "translateZ(0)",
        position: "relative",
        overflow: isSuspendida ? "hidden" : undefined,
        outline: esMovido ? "2px dashed #0d6efd" : undefined,
        outlineOffset: esMovido ? "-2px" : undefined,
      }}
      onClick={() => { if (!esPropuesta && slot) handleClickSlot(cancha, slot); }}
      onMouseEnter={(e) => {
        if (esPropuesta) return;
        setHoverSlot({ horario, canchaID: cancha._id });
        if (isClickable) {
          e.target.style.opacity = "0.8";
          e.target.style.transform = "scale(1.05)";
        }
      }}
      onMouseLeave={(e) => {
        if (esPropuesta) return;
        setHoverSlot({ horario: null, canchaID: null });
        e.target.style.opacity = color === "#28a745" ? "1" : "0.8";
        e.target.style.transform = "scale(1)";
      }}
      title={tooltipPropuesta}
    >
      {slot && slot.estado === "OCUPADO" && (
        <small style={{ color: "white", fontWeight: "bold" }}>
          {isHovered ? `${slot.horaInicio}hs ${cancha.name}` : (() => {
            const nombreFormateado = slot.jugadores?.[0]?.shortName || slot.jugadores?.[0]?.nombre || slot.userFullName || slot.userName || "Ocupado";
            return slot.presencial ? `${nombreFormateado} (Pres.)` : nombreFormateado;
          })()}
        </small>
      )}
      {isSuspendida && (
        <>
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            width: "150%", height: "3px", backgroundColor: "rgba(255,255,255,0.85)",
            transform: "translate(-50%, -50%) rotate(45deg)",
            pointerEvents: "none", zIndex: 2
          }} />
          <div style={{
            position: "absolute", top: "50%", left: "50%",
            width: "150%", height: "3px", backgroundColor: "rgba(255,255,255,0.85)",
            transform: "translate(-50%, -50%) rotate(-45deg)",
            pointerEvents: "none", zIndex: 2
          }} />
          <small style={{ color: "white", fontWeight: "bold", position: "relative", zIndex: 1 }}>
            {isHovered ? `${slot.horaInicio}hs ${cancha.name}` : (slot.jugadores?.[0]?.shortName || slot.jugadores?.[0]?.nombre || slot.userFullName || slot.userName || "Suspendida")}
          </small>
        </>
      )}
      {slot && slot.estado === "BLOQUEADO" && (
        <small style={{ color: "white", fontWeight: "bold" }}>
          {isHovered ? `${slot.horaInicio}hs ${cancha.name}` : (slot.tipo === "OTRO" ? (slot.descripcion || "Otro") : (TIPO_BLOQUEO_LABEL[slot.tipo] || "Bloqueado"))}
        </small>
      )}
      {slot && slot.estado === "DISPONIBLE" && (
        <small style={{ color: "white", fontWeight: "bold" }}>
          {isHovered ? `${slot.horaInicio}hs ${cancha.name}` : "Libre"}
        </small>
      )}
      {slot?.cargosAdicionales?.some((c) => c.concepto === "Luz") && (
        <FontAwesomeIcon
          icon={faLightbulb}
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            zIndex: 3,
            pointerEvents: "none",
            color: "#fff59d",
            fontSize: 14,
            filter: "drop-shadow(0 0 1.5px rgba(0,0,0,0.95)) drop-shadow(0 0 1px rgba(0,0,0,0.6))",
          }}
        />
      )}
    </td>
  );
}, (prevProps, nextProps) => {
  // Solo re-renderizar si el slot cambió o si el hover cambió para ESTA celda específica
  const wasHovered = prevProps.hoverSlot.horario === prevProps.horario && prevProps.hoverSlot.canchaID === prevProps.cancha._id;
  const isHovered = nextProps.hoverSlot.horario === nextProps.horario && nextProps.hoverSlot.canchaID === nextProps.cancha._id;

  return prevProps.slot === nextProps.slot &&
         wasHovered === isHovered &&
         prevProps.esPropuesta === nextProps.esPropuesta;
});

// Componente memoizado para renderizar una fila completa de horario
const HorarioRow = memo(({
  horario,
  canchasDisponibles,
  slotsMatriz,
  hoverSlot,
  setHoverSlot,
  handleClickSlot,
  canchaPropuestaID,
  slotsPropuesta
}) => {
  return (
    <tr>
      <td style={{
        fontWeight: "bold",
        textAlign: "center",
        verticalAlign: "top",
        paddingTop: "2px",
        backgroundColor: hoverSlot.horario === horario ? "#e9ecef" : "#f8f9fa",
        transition: "background-color 0.2s"
      }}>
        {horario}
      </td>
      {canchasDisponibles.map((cancha) => {
        const slotsCancha = slotsMatriz[cancha._id];
        // Buscar slot directamente sin función
        const slot = slotsCancha ? slotsCancha.find((s) => s.horaInicio === horario) : null;
        const mostrarPropuesta = canchaPropuestaID === cancha._id && slotsPropuesta;
        const slotProp = mostrarPropuesta ? slotsPropuesta.find((s) => s.horaInicio === horario) : null;

        return (
          <React.Fragment key={cancha._id}>
            <SlotCell
              cancha={cancha}
              horario={horario}
              slot={slot}
              hoverSlot={hoverSlot}
              setHoverSlot={setHoverSlot}
              handleClickSlot={handleClickSlot}
            />
            {mostrarPropuesta && (
              <SlotCell
                cancha={cancha}
                horario={horario}
                slot={slotProp}
                hoverSlot={hoverSlot}
                setHoverSlot={setHoverSlot}
                handleClickSlot={handleClickSlot}
                esPropuesta
              />
            )}
          </React.Fragment>
        );
      })}
    </tr>
  );
}, (prevProps, nextProps) => {
  // Re-renderizar si cambió el hover que afecta a ESTE horario (incluyendo cambio de cancha dentro del mismo horario)
  const prevIsThisHorario = prevProps.hoverSlot.horario === prevProps.horario;
  const nextIsThisHorario = nextProps.hoverSlot.horario === nextProps.horario;
  const hoverChanged = prevIsThisHorario !== nextIsThisHorario ||
    (prevIsThisHorario && nextIsThisHorario && prevProps.hoverSlot.canchaID !== nextProps.hoverSlot.canchaID);

  // Si cambió la cancha en propuesta o la referencia de slotsPropuesta → re-render
  if (prevProps.canchaPropuestaID !== nextProps.canchaPropuestaID) return false;
  if (prevProps.slotsPropuesta !== nextProps.slotsPropuesta) return false;

  // Verificar si cambió algún slot de este horario en alguna cancha
  let slotsChanged = false;
  for (const cancha of nextProps.canchasDisponibles) {
    const prevSlotsCancha = prevProps.slotsMatriz[cancha._id];
    const nextSlotsCancha = nextProps.slotsMatriz[cancha._id];

    // Si la referencia del array de slots cambió, verificar el slot específico de este horario
    if (prevSlotsCancha !== nextSlotsCancha) {
      // Buscar slots directamente
      const prevSlot = prevSlotsCancha ? prevSlotsCancha.find((s) => s.horaInicio === prevProps.horario) : null;
      const nextSlot = nextSlotsCancha ? nextSlotsCancha.find((s) => s.horaInicio === nextProps.horario) : null;

      // Si la referencia del slot cambió, hubo un cambio
      if (prevSlot !== nextSlot) {
        slotsChanged = true;
        break;
      }
    }
  }

  // No re-renderizar si no cambió nada
  return !hoverChanged && !slotsChanged;
});

// Componente memoizado para la tabla completa de slots
const MatrizSlots = memo(({
  horariosUnicos,
  canchasDisponibles,
  slotsMatriz,
  hoverSlot,
  setHoverSlot,
  handleClickSlot,
  onCanchaClick,
  onHorarioClick,
  isAdminOrGestor,
  esHoy,
  autoScrollActivo,
  onIniciarRecorrido,
  onDetenerRecorrido,
  canchaPropuestaID,
  slotsPropuesta,
  onPropuestaClick,
  onCerrarPropuesta,
  onAplicarPropuesta
}) => {
  const tbodyRef = React.useRef(null);
  const [lineaTop, setLineaTop] = React.useState(null);
  const [lineaLeft, setLineaLeft] = React.useState(0);

  const longPressTimerRef = React.useRef(null);
  const longPressFiredRef = React.useRef(false);


  const calcularPosicion = React.useCallback(() => {
    if (!esHoy || horariosUnicos.length === 0) {
      setLineaTop(null);
      return;
    }
    const tbody = tbodyRef.current;
    if (!tbody) return;
    const filas = tbody.querySelectorAll("tr");
    if (filas.length === 0) return;

    // Medir ancho de la columna Horario (getBoundingClientRect para consistencia con zoom)
    const primerTd = filas[0].querySelector("td");
    const contenedor = tbody.closest("div");
    if (primerTd && contenedor) {
      setLineaLeft(primerTd.getBoundingClientRect().right - contenedor.getBoundingClientRect().left);
    }

    const ahora = new Date();
    const minutosActuales = ahora.getHours() * 60 + ahora.getMinutes();

    // Primer y último horario
    const [h0, m0] = horariosUnicos[0].split(":").map(Number);
    const minInicio = h0 * 60 + m0;
    const minFin = minInicio + horariosUnicos.length * 15;

    if (minutosActuales < minInicio || minutosActuales > minFin) {
      setLineaTop(null);
      return;
    }

    // Posición proporcional dentro del tbody
    const tbodyRect = tbody.getBoundingClientRect();
    const fraccion = (minutosActuales - minInicio) / (minFin - minInicio);
    setLineaTop(fraccion * tbodyRect.height);
  }, [esHoy, horariosUnicos]);

  // Recalcular cada 60 seg y al hacer zoom/resize
  React.useEffect(() => {
    calcularPosicion();
    const intervalo = setInterval(calcularPosicion, 60000);
    window.addEventListener("resize", calcularPosicion);
    return () => {
      clearInterval(intervalo);
      window.removeEventListener("resize", calcularPosicion);
    };
  }, [calcularPosicion]);



  return (
    <div style={{ position: "relative" }}>
      <Table bordered hover className="mb-0" style={{ width: "max-content", minWidth: "100%" }}>
        <thead style={{ backgroundColor: "#f8f9fa" }}>
          <tr>
            <th
              style={{
                minWidth: "100px",
                textAlign: "center",
                backgroundColor: autoScrollActivo ? "#3b82f6" : "#f8f9fa",
                color: autoScrollActivo ? "white" : undefined,
                cursor: "pointer",
                transition: "background-color 0.2s",
                userSelect: "none"
              }}
              onClick={() => { if (longPressFiredRef.current) { longPressFiredRef.current = false; return; } if (autoScrollActivo) { onDetenerRecorrido(); return; } onHorarioClick(); }}
              onMouseDown={() => { longPressFiredRef.current = false; longPressTimerRef.current = setTimeout(() => { longPressFiredRef.current = true; onIniciarRecorrido(); }, 600); }}
              onMouseUp={() => { if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current); }}
              onMouseLeave={() => { if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current); }}
              title={autoScrollActivo ? "Click para detener recorrido" : "Click: partidos en curso | Mantener: recorrido automático"}
            >
              <div style={{fontSize: "18px", lineHeight: 1}}>{autoScrollActivo ? "⏸" : "🎾"}</div>
              {autoScrollActivo ? "Stop" : "Horario"}
            </th>
            {canchasDisponibles.map((cancha) => {
              const esCanchaPropuesta = canchaPropuestaID === cancha._id;
              return (
                <React.Fragment key={cancha._id}>
                  <th
                    style={{
                      minWidth: "120px",
                      textAlign: "center",
                      backgroundColor: hoverSlot.canchaID === cancha._id ? "#e9ecef" : "#f8f9fa",
                      transition: "background-color 0.2s",
                      cursor: isAdminOrGestor ? "pointer" : "default",
                      position: "relative"
                    }}
                    onClick={() => isAdminOrGestor && onCanchaClick(cancha)}
                    title={isAdminOrGestor ? "Haz clic para ver combos eficientes" : ""}
                  >
                    {cancha.name}
                    {isAdminOrGestor && <span style={{fontSize: "10px", marginLeft: "4px"}}>📊</span>}
                    {isAdminOrGestor && (
                      <span
                        style={{
                          fontSize: "12px",
                          marginLeft: "4px",
                          cursor: "pointer",
                          filter: esCanchaPropuesta ? "drop-shadow(0 0 2px #0d6efd)" : undefined,
                        }}
                        onClick={(e) => { e.stopPropagation(); onPropuestaClick && onPropuestaClick(cancha); }}
                        title={esCanchaPropuesta ? "Cerrar propuesta" : "Proponer optimización"}
                      >
                        ✨
                      </span>
                    )}
                    <br />
                    <small className="text-muted">{cancha.codActivo}</small>
                  </th>
                  {esCanchaPropuesta && (
                    <th
                      style={{
                        minWidth: "120px",
                        textAlign: "center",
                        backgroundColor: "#e7f1ff",
                        borderLeft: "2px dashed #0d6efd",
                        position: "relative"
                      }}
                      title="Vista propuesta: huecos de 15/30 min comprimidos"
                    >
                      {cancha.name}
                      <span style={{ fontSize: "10px", marginLeft: "4px", color: "#0d6efd" }}>✨</span>
                      <br />
                      <small style={{ color: "#0d6efd", fontWeight: "bold" }}>PROPUESTA</small>
                      <div style={{ marginTop: "2px", display: "flex", gap: "4px", justifyContent: "center" }}>
                        <Button size="sm" variant="outline-primary" style={{ fontSize: "10px", padding: "1px 6px" }} onClick={(e) => { e.stopPropagation(); onAplicarPropuesta && onAplicarPropuesta(cancha); }}>
                          Aplicar
                        </Button>
                        <Button size="sm" variant="outline-secondary" style={{ fontSize: "10px", padding: "1px 6px" }} onClick={(e) => { e.stopPropagation(); onCerrarPropuesta && onCerrarPropuesta(); }}>
                          Cerrar
                        </Button>
                      </div>
                    </th>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>
        <tbody ref={tbodyRef}>
          {horariosUnicos.map((horario) => (
            <HorarioRow
              key={horario}
              horario={horario}
              canchasDisponibles={canchasDisponibles}
              slotsMatriz={slotsMatriz}
              hoverSlot={hoverSlot}
              setHoverSlot={setHoverSlot}
              handleClickSlot={handleClickSlot}
              canchaPropuestaID={canchaPropuestaID}
              slotsPropuesta={slotsPropuesta}
            />
          ))}
        </tbody>
      </Table>
      {lineaTop !== null && (
        <>
          <div style={{
            position: "absolute",
            top: tbodyRef.current ? tbodyRef.current.offsetTop + lineaTop - 2.5 : 0,
            left: lineaLeft - 2.5,
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#0d6efd",
            zIndex: 11,
            pointerEvents: "none"
          }} />
          <div style={{
            position: "absolute",
            top: tbodyRef.current ? tbodyRef.current.offsetTop + lineaTop : 0,
            left: lineaLeft,
            right: 0,
            height: 1.5,
            backgroundColor: "#0d6efd",
            zIndex: 10,
            pointerEvents: "none",
            boxShadow: "0 0 3px rgba(13, 110, 253, 0.4)"
          }} />
        </>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Solo re-renderizar si realmente cambió algo importante
  return (
    prevProps.horariosUnicos === nextProps.horariosUnicos &&
    prevProps.canchasDisponibles === nextProps.canchasDisponibles &&
    prevProps.slotsMatriz === nextProps.slotsMatriz &&
    prevProps.hoverSlot === nextProps.hoverSlot &&
    prevProps.setHoverSlot === nextProps.setHoverSlot &&
    prevProps.handleClickSlot === nextProps.handleClickSlot &&
    prevProps.esHoy === nextProps.esHoy &&
    prevProps.canchaPropuestaID === nextProps.canchaPropuestaID &&
    prevProps.slotsPropuesta === nextProps.slotsPropuesta
  );
});

function ReservasCanchas() {
  // Contextos
  const rContext = useContext(reservasContext);
  const {
    loading,
    obtenerSlotsTodasCanchas,
    eliminarReserva,
    cancelarReserva,
    suspenderReserva,
    reactivarReserva,
    actualizarReserva,
  } = rContext;

  const actContext = useContext(activoContext);
  const { activoList, obtenerActivosAsOptions } = actContext;
  const [lightweightActivos, setLightweightActivos] = useState(null);

  const aContext = useContext(authContext);
  const { usuario, checkIfSocio, checkIfCanchero, checkIfGestorReservas, checkIfRevisorReservas, checkIfAdmin, globalSettings } = aContext;

  const socket = useContext(SocketContext);

  const bContext = useContext(bloqueosContext);
  const { crearBloqueo, eliminarBloqueo } = bContext;

  // Verificar si es Admin o Gestor de Reservas
  const isAdminOrGestor = checkIfAdmin() || checkIfGestorReservas();

  // Estados locales
  const [vistaActual, setVistaActual] = useState(checkIfRevisorReservas() ? "misReservas" : "disponibilidad"); // "disponibilidad" o "misReservas"
  const [canchasDisponibles, setCanchasDisponibles] = useState([]);
  const [tipoReservaSeleccionado, setTipoReservaSeleccionado] = useState("CANCHA_TENIS"); // "CANCHA_TENIS", "CANCHA_PADEL"
  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date());
  const [tipoJuegoSeleccionado, setTipoJuegoSeleccionado] = useState(null); // null, "SINGLES", "DOBLES"
  const [slotsMatriz, setSlotsMatriz] = useState({}); // { activoID: [slots con color pre-calculado] }
  const [horariosUnicos, setHorariosUnicos] = useState([]); // ["08:00", "09:15", ...]
  const [showModalReserva, setShowModalReserva] = useState(false);
  const [showModalEditar, setShowModalEditar] = useState(false);
  const [showMapaCanchas, setShowMapaCanchas] = useState(false); // Unificado: mapa + lista partidos en curso
  const [slotSeleccionado, setSlotSeleccionado] = useState(null);
  const [showModalInicios, setShowModalInicios] = useState(false); // Modal de inicios disponibles (desde la leyenda)
  const [canchaSeleccionada, setCanchaSeleccionada] = useState(null);
  const [reservaAEditar, setReservaAEditar] = useState(null);
  const [hoverSlot, setHoverSlot] = useState({ horario: null, canchaID: null }); // Para highlight de hover
  const [fechaAbierta, setFechaAbierta] = useState(false); // Si la fecha está abierta para reservas
  const [fechaPresencial, setFechaPresencial] = useState(false); // Si la fecha abierta es presencial
  const [loadingFechaAbierta, setLoadingFechaAbierta] = useState(false); // Loading al cambiar estado de fecha
  const [showModalAnalisis, setShowModalAnalisis] = useState(false); // Modal de análisis de combinaciones
  const [canchaPropuesta, setCanchaPropuesta] = useState(null); // Cancha cuya propuesta de optimización se está mostrando
  const [slotsPropuesta, setSlotsPropuesta] = useState(null); // Slots reordenados para la cancha en propuesta
  const [maxSlotsMovimiento, setMaxSlotsMovimiento] = useState(2); // 1 = máx 15 min, 2 = máx 30 min
  const [autoScrollActivo, setAutoScrollActivo] = useState(false);
  const autoScrollRef = useRef(false);
  const autoScrollTimerRef = useRef(null);

  const detenerRecorridoWeb = useCallback(() => {
    autoScrollRef.current = false;
    setAutoScrollActivo(false);
    if (autoScrollTimerRef.current) { clearInterval(autoScrollTimerRef.current); autoScrollTimerRef.current = null; }
  }, []);

  const iniciarRecorridoWeb = useCallback(() => {
    if (autoScrollRef.current) {
      detenerRecorridoWeb();
      return;
    }

    const container = document.querySelector('.reservas-canchas-container');
    if (!container) return;

    autoScrollRef.current = true;
    setAutoScrollActivo(true);

    autoScrollTimerRef.current = setInterval(() => {
      if (!autoScrollRef.current) {
        clearInterval(autoScrollTimerRef.current);
        autoScrollTimerRef.current = null;
        return;
      }

      const maxY = container.scrollHeight - container.clientHeight;
      if (container.scrollTop >= maxY) {
        detenerRecorridoWeb();
        return;
      }

      container.scrollTop += 3;
    }, 30);
  }, [detenerRecorridoWeb]);

  useEffect(() => {
    const container = document.querySelector('.reservas-canchas-container');
    if (!container) return;
    const handleClick = () => {
      if (autoScrollRef.current) detenerRecorridoWeb();
    };
    container.addEventListener('mousedown', handleClick);
    return () => {
      container.removeEventListener('mousedown', handleClick);
      if (autoScrollTimerRef.current) clearInterval(autoScrollTimerRef.current);
    };
  }, [detenerRecorridoWeb]);
  const [canchaAnalisis, setCanchaAnalisis] = useState(null); // Cancha para analizar
  const [horaFinPicoPorFecha, setHoraFinPicoPorFecha] = useState(null); // Override de horaFinPico por fecha

  // Ref para acceder a slotsMatriz sin causar re-creación de funciones
  const slotsMatrizRef = useRef(slotsMatriz);

  // Actualizar ref cuando slotsMatriz cambia
  useEffect(() => {
    slotsMatrizRef.current = slotsMatriz;
  }, [slotsMatriz]);

  // Refs para fecha y tipo de reserva, necesarios para re-emitir joinSala en reconexiones sin stale closure
  const fechaSeleccionadaRef = useRef(fechaSeleccionada);
  const tipoReservaSeleccionadoRef = useRef(tipoReservaSeleccionado);

  useEffect(() => {
    fechaSeleccionadaRef.current = fechaSeleccionada;
  }, [fechaSeleccionada]);

  useEffect(() => {
    tipoReservaSeleccionadoRef.current = tipoReservaSeleccionado;
  }, [tipoReservaSeleccionado]);

  // Cargar activos ligeros si activoList no esta disponible
  useEffect(() => {
    if (!activoList && !lightweightActivos) {
      obtenerActivosAsOptions('reservable').then(data => {
        if (data) setLightweightActivos(data);
      });
    }
  }, [activoList]);

  // Filtrar canchas reservables según el tipo seleccionado
  useEffect(() => {
    const source = activoList
      ? activoList.filter((activo) => activo.reservable === true)
      : (lightweightActivos || []);
    const canchas = source.filter(
      (activo) => activo.tipoReserva === tipoReservaSeleccionado
    );
    setCanchasDisponibles(canchas);
  }, [activoList, lightweightActivos, tipoReservaSeleccionado]);

  // Limpiar slots y tipo de juego al cambiar tipo de reserva
  useEffect(() => {
    setSlotsMatriz({});
    setHorariosUnicos([]);
    setTipoJuegoSeleccionado(null);
  }, [tipoReservaSeleccionado]);

  // CANCHERO: el tipo de juego es indiferente. Auto-seleccionar un default
  // para que la grilla de disponibilidad se cargue igual (singles por defecto).
  useEffect(() => {
    if (checkIfCanchero() && !tipoJuegoSeleccionado) {
      setTipoJuegoSeleccionado(tipoReservaSeleccionado === "CANCHA_PADEL" ? "SINGLES_PADEL" : "SINGLES");
    }
  }, [tipoReservaSeleccionado, tipoJuegoSeleccionado]);

  // Cargar slots cuando cambia fecha, canchas o tipo de juego
  useEffect(() => {
    if (canchasDisponibles.length > 0 && fechaSeleccionada && tipoJuegoSeleccionado) {
      cargarSlotsTodasCanchas();
    }
  }, [canchasDisponibles, fechaSeleccionada, tipoJuegoSeleccionado]);

  // Verificar si la fecha está abierta cuando cambia la fecha
  useEffect(() => {
    if (fechaSeleccionada) {
      verificarFechaAbierta();
    }
  }, [fechaSeleccionada]);

  // ===== FUNCIONES AUXILIARES (deben estar antes de las funciones de carga) =====

  // Función auxiliar: verifica si un número de slots puede particionarse (TENIS)
  const puedeParticionarseTenis = useCallback((numSlots) => {
    if (numSlots === 0) return true; // 0 es múltiplo de todo
    if (numSlots % 4 === 0) return true; // Solo singles/dobles 60
    if (numSlots % 5 === 0) return true; // Solo dobles 75

    // Probar combinaciones mixtas de 4s y 5s
    for (let cantCuatros = 1; cantCuatros * 4 < numSlots; cantCuatros++) {
      const resto = numSlots - (cantCuatros * 4);
      if (resto > 0 && resto % 5 === 0) return true;
    }

    return false;
  }, []);

  // Función auxiliar: verifica si un número de slots puede particionarse (PÁDEL)
  const puedeParticionarsePadel = useCallback((numSlots) => {
    if (numSlots === 0) return true; // 0 es múltiplo de todo
    if (numSlots % 6 === 0) return true; // Turnos de 90 min (6 slots)
    return false;
  }, []);

  // Validar si un slot puede iniciar una reserva según reglas de particionamiento
  const puedeIniciarReservaEnSlot = useCallback((activoID, horaInicio, slotsOverride = null) => {
    const slotsCancha = slotsOverride || slotsMatrizRef.current[activoID];
    if (!slotsCancha || !tipoJuegoSeleccionado) return { puede: false, motivo: null };

    // Encontrar el índice del slot actual
    const indexSlot = slotsCancha.findIndex((s) => s.horaInicio === horaInicio);
    if (indexSlot === -1) return { puede: false, motivo: null };

    const slot = slotsCancha[indexSlot];

    // Si no está disponible, no puede iniciar
    if (slot.estado !== "DISPONIBLE") return { puede: false, motivo: null };

    // Determinar si la fecha seleccionada es pasada o es hoy con slots ya pasados
    const ahora = new Date();
    const fechaSinHora = new Date(fechaSeleccionada.getFullYear(), fechaSeleccionada.getMonth(), fechaSeleccionada.getDate());
    const hoySinHora = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());

    // Si la fecha es anterior a hoy, todos los slots son pasados
    if (fechaSinHora < hoySinHora) {
      return { puede: false, motivo: "HORARIO_PASADO" };
    }

    // Si es hoy, verificar si el slot específico ya pasó
    const esHoy = fechaSinHora.getTime() === hoySinHora.getTime();
    const minutosActuales = esHoy ? ahora.getHours() * 60 + ahora.getMinutes() : null;

    if (esHoy) {
      const [hSlot, mSlot] = slot.horaInicio.split(":").map(Number);
      if (hSlot * 60 + mSlot < minutosActuales) {
        return { puede: false, motivo: "HORARIO_PASADO" };
      }
    }

    // Determinar cuántos módulos necesita (SINGLES=4, SINGLES_45=3, DOBLES_60=4, DOBLES=5, SINGLES_PADEL=6, DOBLES_PADEL=6)
    let modulosNecesarios = 4; // Default: SINGLES y DOBLES_60
    if (tipoJuegoSeleccionado === "SINGLES_45") modulosNecesarios = 3;
    if (tipoJuegoSeleccionado === "DOBLES") modulosNecesarios = 5;
    if (tipoJuegoSeleccionado === "SINGLES_PADEL" || tipoJuegoSeleccionado === "DOBLES_PADEL") modulosNecesarios = 6;

    // 1. Verificar que haya suficientes slots consecutivos disponibles
    for (let i = 0; i < modulosNecesarios; i++) {
      if (indexSlot + i >= slotsCancha.length) return { puede: false, motivo: "SLOTS_INSUFICIENTES" };
      if (slotsCancha[indexSlot + i].estado !== "DISPONIBLE") return { puede: false, motivo: "SLOTS_INSUFICIENTES" };
    }

    // Si el slot es off-peak, permitir sin chequear particionamiento
    const horaFinPico = horaFinPicoPorFecha ?? globalSettings?.horaFinPico ?? "20:00";
    const [hPico, mPico] = horaFinPico.split(":").map(Number);
    const [hSlotActual, mSlotActual] = horaInicio.split(":").map(Number);
    if (hSlotActual * 60 + mSlotActual >= hPico * 60 + mPico) {
      return { puede: true, motivo: null };
    }

    // 2. Calcular bloques libres ANTES y DESPUÉS de esta reserva
    let slotsLibresAntes = 0;
    for (let i = indexSlot - 1; i >= 0; i--) {
      if (slotsCancha[i].estado === "DISPONIBLE") {
        // Si es hoy, ignorar slots cuya hora ya pasó (tratarlos como no disponibles)
        if (esHoy) {
          const [h, m] = slotsCancha[i].horaInicio.split(":").map(Number);
          if (h * 60 + m < minutosActuales) break;
        }
        slotsLibresAntes++;
      } else {
        break; // Encontramos un slot ocupado o bloqueado
      }
    }

    // *** LÓGICA PRESENCIAL: solo se puede reservar pegado al borde anterior ***
    if (fechaPresencial) {
      if (slotsLibresAntes === 0) {
        // Verificar que el bloqueo (si es lo que está antes) no tenga slots libres antes de él
        const indexAnterior = indexSlot - 1;
        if (indexAnterior >= 0 && slotsCancha[indexAnterior].estado === "BLOQUEADO") {
          // Buscar hacia atrás pasando todos los bloqueos consecutivos
          let idxCheck = indexAnterior;
          while (idxCheck >= 0 && slotsCancha[idxCheck].estado === "BLOQUEADO") {
            idxCheck--;
          }
          // Verificar si hay slots disponibles entre el inicio/horario pasado y el bloqueo
          for (let i = idxCheck; i >= 0; i--) {
            if (slotsCancha[i].estado === "DISPONIBLE") {
              // Si es hoy, ignorar slots cuya hora ya pasó
              if (esHoy) {
                const [h, m] = slotsCancha[i].horaInicio.split(":").map(Number);
                if (h * 60 + m < minutosActuales) break;
              }
              // Hay slot libre antes del bloqueo → el bloqueo no cuenta como borde
              return { puede: false, motivo: "PRESENCIAL_REQUIERE_PEGADO" };
            } else if (slotsCancha[i].estado === "OCUPADO") {
              break; // Reserva encontrada, el bloqueo sí cuenta como borde
            }
            // Si es otro BLOQUEADO, seguir buscando
          }
        }

        // Singles 45: verificar regla de 3 slots encerrados (también en presencial)
        if (tipoJuegoSeleccionado === "SINGLES_45") {
          let slotsLibresDespuesP = 0;
          for (let i = indexSlot + modulosNecesarios; i < slotsCancha.length; i++) {
            if (slotsCancha[i].estado === "DISPONIBLE") {
              slotsLibresDespuesP++;
            } else {
              break;
            }
          }
          const bloqueTotalP = slotsLibresAntes + modulosNecesarios + slotsLibresDespuesP;
          if (!(bloqueTotalP === 3 && slotsLibresAntes === 0 && slotsLibresDespuesP === 0)) {
            return { puede: false, motivo: "SINGLES_45_REQUIERE_3_SLOTS_ENCERRADOS" };
          }
        }

        return { puede: true, motivo: null };
      }
      return { puede: false, motivo: "PRESENCIAL_REQUIERE_PEGADO" };
    }

    let slotsLibresDespues = 0;
    for (let i = indexSlot + modulosNecesarios; i < slotsCancha.length; i++) {
      if (slotsCancha[i].estado === "DISPONIBLE") {
        slotsLibresDespues++;
      } else {
        break; // Encontramos un slot ocupado o bloqueado
      }
    }

    // Determinar si los slots libres de después tienen una "pared con actividad posterior"
    // Solo requiere control si después de los libres hay una reserva, o un bloqueo seguido de actividad
    let despuesRequiereControl = false;
    const indexFinLibres = indexSlot + modulosNecesarios + slotsLibresDespues;
    if (indexFinLibres < slotsCancha.length) {
      const estadoPared = slotsCancha[indexFinLibres].estado;
      if (estadoPared === "OCUPADO") {
        despuesRequiereControl = true;
      } else if (estadoPared === "BLOQUEADO") {
        // Buscar pasando los bloqueos consecutivos
        for (let i = indexFinLibres + 1; i < slotsCancha.length; i++) {
          if (slotsCancha[i].estado !== "BLOQUEADO") {
            // Encontramos actividad posterior (DISPONIBLE u OCUPADO)
            despuesRequiereControl = true;
            break;
          }
        }
        // Si llegamos al final del array, no hay actividad → despuesRequiereControl queda false
      }
    }
    // Si indexFinLibres >= slotsCancha.length → fin del día → despuesRequiereControl queda false

    // *** LÓGICA ESPECIAL PARA SINGLES_45 (3 módulos - 45 min) ***
    // Solo disponible cuando hay exactamente 3 slots encerrados
    if (tipoJuegoSeleccionado === "SINGLES_45") {
      // Debe haber exactamente 3 slots disponibles encerrados (0 antes Y 0 después)
      const bloqueTotal = slotsLibresAntes + modulosNecesarios + slotsLibresDespues;
      if (bloqueTotal === 3 && slotsLibresAntes === 0 && slotsLibresDespues === 0) {
        return { puede: true, motivo: null };
      }
      // Si no cumple la condición de encerrado, no puede usar SINGLES_45
      return { puede: false, motivo: "SINGLES_45_REQUIERE_3_SLOTS_ENCERRADOS" };
    }

    // *** LÓGICA PÁDEL: solo verificar hacia atrás ***
    // Como Pádel tiene una sola duración (6 módulos), basta con que los slots
    // libres antes sean 0 o divisibles por 6 para que se llenen desde el borde.
    if (tipoReservaSeleccionado === "CANCHA_PADEL") {
      if (slotsLibresAntes === 0 || slotsLibresAntes % 6 === 0 || slotsLibresDespues === 0) {
        return { puede: true, motivo: null };
      }
      return { puede: false, motivo: "HUECO_NO_PARTICIONABLE" };
    }

    // *** LÓGICA TENIS: verificar ambos lados ***

    // 3. Calcular el bloque completo (antes + reserva + después)
    const bloqueCompleto = slotsLibresAntes + modulosNecesarios + slotsLibresDespues;
    const bloqueCompletoParticionable = puedeParticionarseTenis(bloqueCompleto);

    // 4. Aplicar REGLA PRINCIPAL: ambos bloques deben poder particionarse
    const antesParticionable = puedeParticionarseTenis(slotsLibresAntes);
    const despuesParticionable = !despuesRequiereControl || puedeParticionarseTenis(slotsLibresDespues);

    if (antesParticionable && despuesParticionable) {
      return { puede: true, motivo: null };
    }

    // 5. Aplicar EXCEPCIÓN: solo si el bloque completo NO era particionable
    // Permite estar pegado a un límite aunque quede hueco no particionable
    if (!bloqueCompletoParticionable) {
      if (slotsLibresAntes === 0 || slotsLibresDespues === 0) {
        return { puede: true, motivo: null };
      }
    }

    // No cumple ni la regla ni la excepción
    return { puede: false, motivo: "HUECO_NO_PARTICIONABLE" };
  }, [tipoJuegoSeleccionado, tipoReservaSeleccionado, puedeParticionarseTenis, puedeParticionarsePadel, fechaSeleccionada, globalSettings, horaFinPicoPorFecha, fechaPresencial]);

  // Obtener color según estado del slot
  const getColorSlot = useCallback((slot, activoID, slotsOverride = null) => {
    if (!slot) return "#f8f9fa"; // gris claro por defecto
    switch (slot.estado) {
      case "DISPONIBLE":
        // Verificar si puede iniciar una reserva desde este slot
        const resultado = puedeIniciarReservaEnSlot(activoID, slot.horaInicio, slotsOverride);
        if (resultado.puede) {
          return "#28a745"; // verde fuerte - puede iniciar reserva
        } else {
          return "#d4edda"; // verde muy claro - disponible pero no puede iniciar
        }
      case "OCUPADO":
      case "SUSPENDIDA":
        // El tono se asigna en asignarColoresCancha(), aquí solo el color base
        if (slot.userID === usuario?._id) {
          return slot._tonoOscuro ? "#e6ad00" : "#ffc107"; // amarillo oscuro / amarillo
        }
        return slot._tonoOscuro ? "#c82333" : "#dc3545"; // rojo oscuro / rojo
      case "BLOQUEADO":
        return "#6c757d"; // gris
      default:
        return "#f8f9fa";
    }
  }, [puedeIniciarReservaEnSlot, usuario]);

  // Slots donde SÍ se puede iniciar una reserva, de todas las canchas en una sola
  // lista ordenada por horario. Dentro del mismo horario quedan en el orden de
  // canchasDisponibles, porque Array.sort es estable.
  //
  // Depende de slotsMatriz (el state, no el ref) para recalcular ante cualquier
  // evento de socket. Y le pasa slotsCancha como slotsOverride porque
  // puedeIniciarReservaEnSlot lee de slotsMatrizRef, que se actualiza en un
  // useEffect y por lo tanto va un render atrás.
  const iniciosOrdenados = useMemo(() => {
    if (!tipoJuegoSeleccionado) return [];

    const lista = [];
    for (const cancha of canchasDisponibles) {
      const slotsCancha = slotsMatriz[cancha._id];
      if (!Array.isArray(slotsCancha)) continue;

      for (const slot of slotsCancha) {
        if (slot.estado !== "DISPONIBLE") continue;
        if (!puedeIniciarReservaEnSlot(cancha._id, slot.horaInicio, slotsCancha).puede) continue;
        lista.push({
          cancha,
          slot,
          horaInicio: slot.horaInicio,
          horaFin: calcularHoraFinInicio(slot.horaInicio, tipoJuegoSeleccionado),
        });
      }
    }

    // "HH:mm" con cero a la izquierda ordena bien como string
    lista.sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
    return lista;
  }, [canchasDisponibles, slotsMatriz, tipoJuegoSeleccionado, puedeIniciarReservaEnSlot]);

  // Click en un inicio del modal. Mismas validaciones que el click en la grilla;
  // no hace falta revalidar el motivo, estos slots ya pasaron puedeIniciarReservaEnSlot.
  const handleInicioClick = useCallback((cancha, slot) => {
    if (checkIfCanchero()) return; // CANCHERO es solo visualización

    if (checkIfSocio() && !checkIfAdmin() && !checkIfGestorReservas()) {
      if (!fechaAbierta) {
        Swal.fire({
          icon: "warning",
          title: "Fecha no disponible",
          text: "Esta fecha no está abierta para reservas de socios. Por favor contacta al administrador.",
        });
        return;
      }
      if (fechaPresencial) {
        Swal.fire({
          icon: "warning",
          title: "Reserva presencial",
          text: "Esta fecha es solo para reservas presenciales.",
        });
        return;
      }
    }

    setShowModalInicios(false);
    setCanchaSeleccionada(cancha);
    setSlotSeleccionado(slot);
    setShowModalReserva(true);
  }, [checkIfCanchero, checkIfSocio, checkIfAdmin, checkIfGestorReservas, fechaAbierta, fechaPresencial]);

  // Obtener slot para una cancha y horario específico
  const getSlotParaCanchaYHorario = useCallback((activoID, horaInicio, slotsOverride = null) => {
    const slotsCancha = slotsOverride || slotsMatrizRef.current[activoID];
    if (!slotsCancha) return null;
    return slotsCancha.find((s) => s.horaInicio === horaInicio);
  }, []);

  // Asignar colores a los slots de una cancha, alternando tono entre reservas consecutivas
  const asignarColoresCancha = useCallback((slotsCancha, activoID) => {
    let tonoOscuro = false;
    let lastReservaID = null;
    return slotsCancha.map((slot) => {
      // Alternar tono cada vez que cambia la reserva
      const esOcupadoOSuspendida = slot.estado === "OCUPADO" || slot.estado === "SUSPENDIDA";
      if (esOcupadoOSuspendida && slot.reservaID !== lastReservaID) {
        if (lastReservaID !== null) tonoOscuro = !tonoOscuro;
        lastReservaID = slot.reservaID;
      } else if (!esOcupadoOSuspendida) {
        lastReservaID = null;
      }
      const slotConTono = esOcupadoOSuspendida ? { ...slot, _tonoOscuro: tonoOscuro } : slot;
      return { ...slotConTono, _color: getColorSlot(slotConTono, activoID, slotsCancha) };
    });
  }, [getColorSlot]);

  // ===== FIN FUNCIONES AUXILIARES =====

  // Ref para asignarColoresCancha: evita stale closure en funciones async de carga
  const asignarColoresCanchaRef = useRef(asignarColoresCancha);
  asignarColoresCanchaRef.current = asignarColoresCancha;

  // Cargar slots solo de una cancha específica (optimización para Socket.io)
  const cargarSlotsCanchaEspecifica = useCallback(async (activoID) => {
    try {
      console.log("🔄 [SOCKET] Actualizando cancha:", activoID);

      // Formatear fecha en zona horaria local
      const year = fechaSeleccionada.getFullYear();
      const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
      const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
      const fechaISO = `${year}-${month}-${day}`;

      // Hacer fetch directo SIN usar la función del contexto (que cambia loading state y causa re-render)
      const token = localStorage.getItem("token");
      const response = await clienteAxios.get("/cancha-reservas/disponibilidad-canchas", {
        params: {
          activoIDs: JSON.stringify([activoID]),
          fecha: fechaISO
        },
        headers: { Authorization: token }
      });
      const slots = response.data.data;

      // Actualizar slot por slot, manteniendo referencias de los que no cambiaron
      setSlotsMatriz(prevSlots => {
        const slotsAnteriores = prevSlots[activoID] || [];
        const slotsNuevos = slots[activoID] || [];

        // PRE-CALCULAR colores para todos los slots nuevos (con tono alternado)
        const slotsConColor = asignarColoresCanchaRef.current(slotsNuevos, activoID);

        // Comparar slot por slot manteniendo referencias cuando no cambió NADA (ni datos ni color)
        const slotsActualizados = slotsConColor.map((slotNuevo, index) => {
          const slotAnterior = slotsAnteriores[index];

          // Si no había slot anterior, usar el nuevo
          if (!slotAnterior) return slotNuevo;

          // Comparar propiedades relevantes Y el color
          const cambio =
            slotAnterior.estado !== slotNuevo.estado ||
            slotAnterior.reservaID !== slotNuevo.reservaID ||
            slotAnterior.userID !== slotNuevo.userID ||
            slotAnterior._color !== slotNuevo._color ||
            JSON.stringify(slotAnterior.jugadores) !== JSON.stringify(slotNuevo.jugadores);

          // Si no cambió NADA (ni datos ni color), mantener la referencia anterior
          return cambio ? slotNuevo : slotAnterior;
        });

        return {
          ...prevSlots,
          [activoID]: slotsActualizados
        };
      });
    } catch (error) {
      console.error("❌ Error al actualizar cancha específica:", activoID, error);
      // Silenciar el error para evitar que rompa la UI
      // Si es un timeout por inactividad, no es crítico
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.warn("⚠️ Timeout al actualizar cancha - probablemente por inactividad de la conexión");
      }
    }
  }, [fechaSeleccionada]);

  // Unirse a la sala de socket correspondiente a la fecha y tipo de reserva
  useEffect(() => {
    if (!socket || !fechaSeleccionada) return;
    const year = fechaSeleccionada.getFullYear();
    const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
    const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
    const fecha = `${year}-${month}-${day}`;
    socket.emit("joinSala", { fecha, tipoReserva: tipoReservaSeleccionado });
  }, [socket, fechaSeleccionada, tipoReservaSeleccionado]);

  // Re-emitir joinSala en cada (re)conexion del socket para que el nuevo socket.id
  // entre a la sala correcta. Cubre el caso de cambio de pestana o minimizacion del
  // navegador, donde el browser puede cortar o throttlear el websocket en background.
  useEffect(() => {
    if (!socket) return;
    const handleConnect = () => {
      const d = fechaSeleccionadaRef.current;
      if (!d) return;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      socket.emit("joinSala", {
        fecha: `${year}-${month}-${day}`,
        tipoReserva: tipoReservaSeleccionadoRef.current,
      });
    };
    socket.on("connect", handleConnect);
    return () => socket.off("connect", handleConnect);
  }, [socket]);

  // Escuchar eventos Socket.io para actualizar en tiempo real
  useEffect(() => {
    console.log("socket");
    console.log(socket);
    
    if (!socket || !tipoJuegoSeleccionado || !fechaSeleccionada) return;

    // Helper para comparar fechas (ignora timezone)
    const esMismaFecha = (fecha1, fecha2) => {
      // Parsear fecha1 del evento - manejar ambos formatos:
      // - Con 'T': "2025-12-21T00:00:00.000Z" (ISO completo)
      // - Sin 'T': "2025-12-21" (solo fecha YYYY-MM-DD)
      let d1;
      if (typeof fecha1 === 'string') {
        // Extraer solo la parte YYYY-MM-DD (funciona para ambos formatos)
        const fechaStr = fecha1.includes('T') ? fecha1.split('T')[0] : fecha1;
        const [year, month, day] = fechaStr.split('-').map(Number);
        d1 = new Date(year, month - 1, day); // Crear fecha en zona local
      } else {
        d1 = new Date(fecha1);
      }

      // fecha2 ya es un objeto Date local
      const d2 = fecha2 instanceof Date ? fecha2 : new Date(fecha2);

      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      );
    };

    // Convierte "HH:MM" a minutos para comparar
    const parseTime = (t) => {
      const [h, m] = t.split(":").map(Number);
      return h * 60 + m;
    };

    // Actualiza slots localmente sin GET
    const actualizarSlotLocal = (nuevoEstado) => (data) => {
      if (data?.reserva?.fecha && !esMismaFecha(data.reserva.fecha, fechaSeleccionada)) {
        return;
      }

      const { activoID, horaInicio, horaFin, shortName, tipoJuego, userID, _id } = data?.reserva || {};

      if (activoID && horaInicio && horaFin) {
        const rInicio = parseTime(horaInicio);
        const rFin = parseTime(horaFin);

        setSlotsMatriz(prev => {
          const slotsCancha = prev[activoID];
          if (!slotsCancha) return prev;

          const slotsModificados = slotsCancha.map(slot => {
            const sInicio = parseTime(slot.horaInicio);
            const sFin = parseTime(slot.horaFin);

            if (sInicio >= rInicio && sFin <= rFin) {
              if (nuevoEstado === "DISPONIBLE") {
                return { horaInicio: slot.horaInicio, horaFin: slot.horaFin, estado: "DISPONIBLE" };
              }
              return {
                ...slot,
                estado: nuevoEstado,
                reservaHoraInicio: horaInicio,
                reservaHoraFin: horaFin,
                jugadores: [{ shortName: shortName || "" }],
                tipoJuego,
                userID,
                reservaID: _id,
              };
            }
            return slot;
          });

          // Recalcular colores (tono alterno + color por estado)
          const slotsConColor = asignarColoresCanchaRef.current(slotsModificados, activoID);
          return { ...prev, [activoID]: slotsConColor };
        });
      } else {
        cargarSlotsCanchaEspecifica(activoID || null);
      }
    };

    const handleNuevaReserva = actualizarSlotLocal("OCUPADO");
    const handleReservaCancelada = actualizarSlotLocal("DISPONIBLE");
    const handleReservaEliminada = actualizarSlotLocal("DISPONIBLE");
    const handleReservaSuspendida = actualizarSlotLocal("SUSPENDIDA");
    const handleReservaReactivada = actualizarSlotLocal("OCUPADO");

    // Para reservaActualizada fallback a GET (no sabemos qué cambió)
    const handleReservaActualizada = (data) => {
      if (data?.reserva?.fecha && !esMismaFecha(data.reserva.fecha, fechaSeleccionada)) return;
      cargarSlotsCanchaEspecifica(data?.reserva?.activoID || null);
    };

    // Handler para eventos de bloqueos (crear, eliminar)
    const handleBloqueoChange = (data) => {
      console.log("🔄 Socket: Evento de bloqueo recibido");

      if (data?.bloqueo?.activoID && data?.bloqueo?.fechaInicio && data?.bloqueo?.fechaFin) {
        const { activoID, fechaInicio, fechaFin } = data.bloqueo;
        // Comparar solo por YYYY-MM-DD para evitar issues de timezone (fechaInicio/fechaFin
        // llegan como ISO UTC y fechaSeleccionada es un Date local).
        const toYMD = (v) => {
          if (typeof v === 'string') {
            return v.includes('T') ? v.split('T')[0] : v;
          }
          const d = v instanceof Date ? v : new Date(v);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        const inicioYMD = toYMD(fechaInicio);
        const finYMD = toYMD(fechaFin);
        const actualYMD = toYMD(fechaSeleccionada);

        if (actualYMD >= inicioYMD && actualYMD <= finYMD) {
          cargarSlotsCanchaEspecifica(activoID);
        }
      } else {
        console.warn("⚠️ Evento sin datos completos, recargando todo");
        cargarSlotsTodasCanchas();
      }
    };

    // Handler para eventos de fecha abierta/cerrada
    const handleFechaAbiertaChange = (data) => {
      console.log("🔄 Socket: Evento fechaAbierta recibido", data);
      if (data?.fecha && esMismaFecha(data.fecha, fechaSeleccionada)) {
        console.log("✅ La fecha coincide, actualizando estado a: abierta");
        setFechaAbierta(true);
        setFechaPresencial(data.presencial || false);
        setHoraFinPicoPorFecha(null);
      }
    };

    const handleFechaCerradaChange = (data) => {
      console.log("🔄 Socket: Evento fechaCerrada recibido", data);
      if (data?.fecha && esMismaFecha(data.fecha, fechaSeleccionada)) {
        console.log("✅ La fecha coincide, actualizando estado a: cerrada");
        setFechaAbierta(false);
        setFechaPresencial(false);
        setHoraFinPicoPorFecha(null);
      }
    };

    const handleFechaPresencialChange = (data) => {
      console.log("🔄 Socket: Evento fechaPresencialActualizada recibido", data);
      if (data?.fecha && esMismaFecha(data.fecha, fechaSeleccionada)) {
        console.log("✅ La fecha coincide, actualizando presencial a:", data.presencial);
        setFechaPresencial(data.presencial || false);
      }
    };

    const handleFechaHoraFinPicoChange = (data) => {
      console.log("🔄 Socket: Evento fechaHoraFinPicoActualizada recibido", data);
      if (data?.fecha && esMismaFecha(data.fecha, fechaSeleccionada)) {
        console.log("✅ La fecha coincide, actualizando horaFinPico a:", data.horaFinPico);
        setHoraFinPicoPorFecha(data.horaFinPico || null);
      }
    };

    // Suscribirse a eventos
    socket.on("nuevaReserva", handleNuevaReserva);
    socket.on("reservaActualizada", handleReservaActualizada);
    socket.on("reservaCancelada", handleReservaCancelada);
    socket.on("reservaEliminada", handleReservaEliminada);
    socket.on("reservaSuspendida", handleReservaSuspendida);
    socket.on("reservaReactivada", handleReservaReactivada);
    socket.on("nuevoBloqueo", handleBloqueoChange);
    socket.on("bloqueoEliminado", handleBloqueoChange);
    socket.on("fechaAbierta", handleFechaAbiertaChange);
    socket.on("fechaCerrada", handleFechaCerradaChange);
    socket.on("fechaPresencialActualizada", handleFechaPresencialChange);
    socket.on("fechaHoraFinPicoActualizada", handleFechaHoraFinPicoChange);

    // Handler para cuando un gestor aplica una propuesta de optimización
    const handlePropuestaAplicada = (data) => {
      console.log("🔄 Socket: Evento propuestaAplicada recibido", data);
      if (data?.fecha && !esMismaFecha(data.fecha, fechaSeleccionada)) return;
      if (!data?.activoID) return;
      cargarSlotsCanchaEspecifica(data.activoID);
    };
    socket.on("propuestaAplicada", handlePropuestaAplicada);

    // Handler para refrescar slots (cron cada 15 min) - recarga cancha por cancha para evitar parpadeo
    const handleRefrescarSlots = (data) => {
      // Solo refrescar si el cliente está mirando la fecha de hoy
      if (data?.fecha && !esMismaFecha(data.fecha, fechaSeleccionada)) {
        return;
      }
      console.log("🔄 Socket: Evento refrescarSlots recibido (cron)");
      const activoIDs = Object.keys(slotsMatrizRef.current);
      activoIDs.forEach((activoID) => {
        cargarSlotsCanchaEspecifica(activoID);
      });
    };
    socket.on("refrescarSlots", handleRefrescarSlots);

    // Cleanup
    return () => {
      socket.off("nuevaReserva", handleNuevaReserva);
      socket.off("reservaActualizada", handleReservaActualizada);
      socket.off("reservaCancelada", handleReservaCancelada);
      socket.off("reservaEliminada", handleReservaEliminada);
      socket.off("reservaSuspendida", handleReservaSuspendida);
      socket.off("reservaReactivada", handleReservaReactivada);
      socket.off("nuevoBloqueo", handleBloqueoChange);
      socket.off("bloqueoEliminado", handleBloqueoChange);
      socket.off("fechaAbierta", handleFechaAbiertaChange);
      socket.off("fechaCerrada", handleFechaCerradaChange);
      socket.off("fechaPresencialActualizada", handleFechaPresencialChange);
      socket.off("fechaHoraFinPicoActualizada", handleFechaHoraFinPicoChange);
      socket.off("propuestaAplicada", handlePropuestaAplicada);
      socket.off("refrescarSlots", handleRefrescarSlots);
    };
  }, [socket, tipoJuegoSeleccionado, fechaSeleccionada, cargarSlotsCanchaEspecifica]);

  const cargarSlotsTodasCanchas = useCallback(async () => {
    try {
      console.log("📅 Cargando slots para fecha:", fechaSeleccionada);
      const activoIDs = canchasDisponibles.map((c) => c._id);

      // Formatear fecha en zona horaria local (evitar problemas con UTC)
      const year = fechaSeleccionada.getFullYear();
      const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
      const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
      const fechaISO = `${year}-${month}-${day}`;

      console.log("🏟️ Canchas:", activoIDs);
      console.log("📆 Fecha ISO enviada al servidor:", fechaISO);
      const slots = await obtenerSlotsTodasCanchas(activoIDs, fechaISO);
      console.log("📊 Slots recibidos del servidor:", slots);

      // PRE-CALCULAR colores para todos los slots de todas las canchas (con tono alternado)
      const slotsConColor = {};
      Object.keys(slots).forEach((activoID) => {
        slotsConColor[activoID] = asignarColoresCanchaRef.current(slots[activoID], activoID);
      });

      // Actualizar con los slots que tienen color pre-calculado
      setSlotsMatriz(slotsConColor);

      // Extraer horarios únicos (asumiendo que todas las canchas tienen los mismos horarios)
      if (Object.keys(slots).length > 0) {
        const primeraCancha = Object.values(slots)[0];
        if (primeraCancha && primeraCancha.length > 0) {
          const horarios = primeraCancha.map((slot) => slot.horaInicio);
          setHorariosUnicos([...horarios]);
          console.log("⏰ Horarios únicos actualizados:", horarios);
        }
      }
      console.log("✨ Matriz actualizada correctamente");
    } catch (error) {
      console.error("❌ Error al cargar slots:", error);
      // Silenciar el error para evitar que rompa la UI
      // Si es un timeout por inactividad, no es crítico
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.warn("⚠️ Timeout al cargar slots - probablemente por inactividad de la conexión");
      }
    }
  }, [fechaSeleccionada, canchasDisponibles, obtenerSlotsTodasCanchas]);

  // Ref para evitar stale closure en el handler de visibilitychange: verificarFechaAbierta
  // se recrea en cada render, pero no queremos re-suscribir el listener cada vez.
  const verificarFechaAbiertaRef = useRef();
  verificarFechaAbiertaRef.current = () => verificarFechaAbierta();

  // Al volver a la pestana despues de minimizar o cambiar de tab, refrescar los
  // slots para ponerse al dia con eventos que se perdieron mientras el websocket
  // estaba throttleado o cerrado por el navegador. Tambien revalidamos el estado
  // de fecha abierta/cerrada por si el cron disparo en background y el evento
  // socket se perdio. Paralelo al AppState handler de mobile.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        cargarSlotsTodasCanchas();
        verificarFechaAbiertaRef.current();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cargarSlotsTodasCanchas]);

  // Re-asignar colores cuando cambia horaFinPicoPorFecha o fechaPresencial (sin refetch del servidor)
  useEffect(() => {
    setSlotsMatriz(prev => {
      if (Object.keys(prev).length === 0) return prev;
      const recalculado = {};
      Object.keys(prev).forEach(activoID => {
        // Sacar colores viejos y re-calcular
        const slotsLimpios = prev[activoID].map(s => {
          const { _color, _tonoOscuro, ...rest } = s;
          return rest;
        });
        recalculado[activoID] = asignarColoresCancha(slotsLimpios, activoID);
      });
      return recalculado;
    });
  }, [horaFinPicoPorFecha, fechaPresencial, asignarColoresCancha]);

  // Verificar si una fecha está abierta para reservas
  const verificarFechaAbierta = async () => {
    try {
      const token = localStorage.getItem("token");
      const year = fechaSeleccionada.getFullYear();
      const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
      const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
      const fechaISO = `${year}-${month}-${day}`;

      const response = await clienteAxios.get(`/cancha-fecha-abierta/check/${fechaISO}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.success) {
        setFechaAbierta(response.data.data.abierta);
        setFechaPresencial(response.data.data.presencial || false);
        setHoraFinPicoPorFecha(response.data.data.horaFinPico || null);
      }
    } catch (error) {
      console.error("Error al verificar fecha abierta:", error);
      setFechaAbierta(false);
      setFechaPresencial(false);
      setHoraFinPicoPorFecha(null);
    }
  };

  // Alternar estado de fecha abierta (solo Admin/Gestor)
  const toggleFechaAbierta = async () => {
    setLoadingFechaAbierta(true);
    try {
      const token = localStorage.getItem("token");
      const year = fechaSeleccionada.getFullYear();
      const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
      const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
      const fechaISO = `${year}-${month}-${day}`;

      if (fechaAbierta) {
        // Cerrar fecha
        await clienteAxios.delete(`/cancha-fecha-abierta/${fechaISO}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFechaAbierta(false);
        setFechaPresencial(false);
        Swal.fire({
          icon: "success",
          title: "Fecha cerrada",
          text: "La fecha ha sido cerrada para reservas de socios",
          timer: 2000,
        });
      } else {
        // Abrir fecha
        await clienteAxios.post(
          "/cancha-fecha-abierta",
          { fecha: fechaISO },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFechaAbierta(true);
        Swal.fire({
          icon: "success",
          title: "Fecha abierta",
          text: "La fecha ha sido abierta para reservas de socios",
          timer: 2000,
        });
      }
    } catch (error) {
      console.error("Error al alternar fecha abierta:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al cambiar el estado de la fecha",
      });
    } finally {
      setLoadingFechaAbierta(false);
    }
  };

  // Alternar estado presencial de una fecha abierta (solo Admin/Gestor)
  const togglePresencial = async () => {
    setLoadingFechaAbierta(true);
    try {
      const token = localStorage.getItem("token");
      const year = fechaSeleccionada.getFullYear();
      const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
      const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
      const fechaISO = `${year}-${month}-${day}`;

      const nuevoPresencial = !fechaPresencial;
      await clienteAxios.put(
        `/cancha-fecha-abierta/${fechaISO}`,
        { presencial: nuevoPresencial },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFechaPresencial(nuevoPresencial);
      Swal.fire({
        icon: "success",
        title: nuevoPresencial ? "Presencial activado" : "Presencial desactivado",
        text: nuevoPresencial
          ? "La fecha ha sido marcada como presencial"
          : "La fecha ha sido desmarcada como presencial",
        timer: 2000,
      });
    } catch (error) {
      console.error("Error al alternar presencial:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al cambiar el estado presencial",
      });
    } finally {
      setLoadingFechaAbierta(false);
    }
  };

  // Cambiar horaFinPico de la fecha seleccionada (solo Admin/Gestor)
  const handleChangeHoraFinPico = async (nuevoValor) => {
    try {
      const token = localStorage.getItem("token");
      const year = fechaSeleccionada.getFullYear();
      const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
      const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
      const fechaISO = `${year}-${month}-${day}`;

      // Si el valor es igual al global, enviar null para resetear
      const globalDefault = globalSettings?.horaFinPico || "20:00";
      const horaFinPico = nuevoValor === globalDefault ? null : nuevoValor;

      await clienteAxios.put(
        `/cancha-fecha-abierta/${fechaISO}/hora-fin-pico`,
        { horaFinPico },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHoraFinPicoPorFecha(horaFinPico);
    } catch (error) {
      console.error("Error al actualizar horaFinPico:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Error al actualizar hora fin pico",
      });
    }
  };

  // Crear bloqueo unitario para un slot específico (solo Admin/Gestor)
  const crearBloqueoUnitario = async (cancha, slot) => {
    const result = await Swal.fire({
      title: "Bloquear slot",
      html: `
        <div style="text-align: left;">
          <p><strong>Cancha:</strong> ${cancha.name}</p>
          <p><strong>Horario:</strong> ${slot.horaInicio}</p>
          <p><strong>Fecha:</strong> ${getFechaFormateada()}</p>
          <hr style="margin: 15px 0;">
          <div style="margin-bottom: 15px;">
            <label for="swal-tipo" style="display: block; margin-bottom: 5px; font-weight: bold;">Motivo del bloqueo *</label>
            <select id="swal-tipo" class="swal2-input" style="width: 100%; padding: 8px; border: 1px solid #d9d9d9; border-radius: 4px;">
              <option value="MANTENIMIENTO">Mantenimiento</option>
              <option value="EVENTO_PRIVADO">Evento Privado</option>
              <option value="CLIMA">Clima</option>
              <option value="CLASES">Clases</option>
              <option value="OTRO" selected>Otro</option>
            </select>
          </div>
          <div style="margin-bottom: 15px;">
            <label for="swal-descripcion" style="display: block; margin-bottom: 5px; font-weight: bold;">Descripción *</label>
            <textarea
              id="swal-descripcion"
              class="swal2-input"
              placeholder="Describe el motivo del bloqueo..."
              style="width: 100%; height: 80px; padding: 8px; border: 1px solid #d9d9d9; border-radius: 4px; resize: vertical;"
            ></textarea>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Bloquear",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#6c757d",
      cancelButtonColor: "#6c757d",
      preConfirm: () => {
        const tipo = document.getElementById("swal-tipo").value;
        const descripcion = document.getElementById("swal-descripcion").value.trim();

        if (!tipo) {
          Swal.showValidationMessage("Debes seleccionar un motivo");
          return false;
        }

        if (!descripcion) {
          Swal.showValidationMessage("Debes ingresar una descripción");
          return false;
        }

        if (tipo === "OTRO" && descripcion.length > 15) {
          Swal.showValidationMessage("Cuando el motivo es 'Otro', la descripción no puede superar los 15 caracteres para su correcta visualización en los slots");
          return false;
        }

        return { tipo, descripcion };
      },
    });

    if (result.isConfirmed) {
      try {
        const year = fechaSeleccionada.getFullYear();
        const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
        const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
        const fechaISO = `${year}-${month}-${day}`;

        // Calcular horaFin (15 minutos después de horaInicio)
        const [horas, minutos] = slot.horaInicio.split(":").map(Number);
        const horaFinDate = new Date(2000, 0, 1, horas, minutos + 15);
        const horaFin = `${String(horaFinDate.getHours()).padStart(2, "0")}:${String(horaFinDate.getMinutes()).padStart(2, "0")}`;

        const bloqueoData = {
          activoID: cancha._id,
          codActivo: cancha.codActivo,
          nameActivo: cancha.name,
          fechaInicio: `${fechaISO}T00:00:00.000Z`,
          fechaFin: `${fechaISO}T23:59:59.999Z`,
          horaInicio: slot.horaInicio,
          horaFin: horaFin,
          tipo: result.value.tipo,
          descripcion: result.value.descripcion,
        };

        await crearBloqueo(bloqueoData);
        cargarSlotsTodasCanchas();
      } catch (error) {
        console.error("Error al crear bloqueo:", error);
      }
    }
  };

  // Eliminar bloqueo unitario (solo Admin/Gestor)
  const eliminarBloqueoUnitario = async (slot) => {
    const result = await Swal.fire({
      title: "¿Desbloquear este slot?",
      text: "Esta acción eliminará el bloqueo y el slot quedará disponible",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, desbloquear",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      try {
        if (!slot.bloqueoID) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "No se encontró el ID del bloqueo",
          });
          return;
        }

        await eliminarBloqueo(slot.bloqueoID);
        cargarSlotsTodasCanchas();
      } catch (error) {
        console.error("Error al eliminar bloqueo:", error);
      }
    }
  };

  // Handlers
  const handleCambioFecha = (e) => {
    const nuevaFecha = new Date(e.target.value + "T00:00:00");
    setFechaSeleccionada(nuevaFecha);
  };

  const handleDiaAnterior = () => {
    const nuevaFecha = new Date(fechaSeleccionada);
    nuevaFecha.setDate(nuevaFecha.getDate() - 1);
    setFechaSeleccionada(nuevaFecha);
  };

  const handleDiaSiguiente = () => {
    const nuevaFecha = new Date(fechaSeleccionada);
    nuevaFecha.setDate(nuevaFecha.getDate() + 1);
    setFechaSeleccionada(nuevaFecha);
  };

  // Handler para click en header de cancha (análisis de combinaciones)
  const handleCanchaClick = (cancha) => {
    setCanchaAnalisis(cancha);
    setShowModalAnalisis(true);
  };

  // Handler para click en ✨: toggle propuesta de optimización para esa cancha
  const handlePropuestaOptimizacionClick = useCallback(async (cancha) => {
    if (canchaPropuesta && canchaPropuesta._id === cancha._id) {
      setCanchaPropuesta(null);
      setSlotsPropuesta(null);
      return;
    }

    // Preguntar máximo de movimiento
    const { value: maxSlotsElegido } = await Swal.fire({
      title: "Máximo de movimiento por bloque",
      text: "¿Hasta cuánto podés correr cada reserva/mantenimiento?",
      input: "radio",
      inputOptions: {
        "1": "Hasta 15 min (1 slot)",
        "2": "Hasta 30 min (2 slots)",
      },
      inputValue: "2",
      showCancelButton: true,
      confirmButtonText: "Calcular propuesta",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0d6efd",
      inputValidator: (value) => {
        if (!value) return "Elegí una opción";
      },
    });
    if (!maxSlotsElegido) return;
    const maxSlots = Number(maxSlotsElegido);

    const slotsOrig = slotsMatriz[cancha._id] || [];
    const propuesta = calcularPropuestaOptimizacion(slotsOrig, maxSlots);
    const propuestaConColor = asignarColoresCanchaRef.current
      ? asignarColoresCanchaRef.current(propuesta, cancha._id)
      : propuesta;
    setMaxSlotsMovimiento(maxSlots);
    setCanchaPropuesta(cancha);
    setSlotsPropuesta(propuestaConColor);
  }, [canchaPropuesta, slotsMatriz, maxSlotsMovimiento]);

  const handleCerrarPropuesta = useCallback(() => {
    setCanchaPropuesta(null);
    setSlotsPropuesta(null);
  }, []);

  const handleAplicarPropuesta = useCallback(async () => {
    if (!canchaPropuesta || !slotsPropuesta) return;

    const slotsOrig = slotsMatriz[canchaPropuesta._id] || [];

    // Para cada reservaID/bloqueoID movible en slotsOrig, tomar su primer slot (horaInicio original)
    // y buscar en slotsPropuesta dónde aparece ahora para obtener la nueva horaInicio.
    const origPrimerSlot = new Map(); // key: "reserva:<id>" | "bloqueo:<id>", value: slot original
    slotsOrig.forEach((s) => {
      if (s.estado === "OCUPADO" || s.estado === "SUSPENDIDA") {
        const k = `reserva:${s.reservaID}`;
        if (!origPrimerSlot.has(k)) origPrimerSlot.set(k, s);
      } else if (s.estado === "BLOQUEADO" && s.tipo === "MANTENIMIENTO") {
        const k = `bloqueo:${s.bloqueoID}`;
        if (!origPrimerSlot.has(k)) origPrimerSlot.set(k, s);
      }
    });

    const propPrimerSlot = new Map();
    slotsPropuesta.forEach((s) => {
      if (s.estado === "OCUPADO" || s.estado === "SUSPENDIDA") {
        const k = `reserva:${s.reservaID}`;
        if (!propPrimerSlot.has(k)) propPrimerSlot.set(k, s);
      } else if (s.estado === "BLOQUEADO" && s.tipo === "MANTENIMIENTO") {
        const k = `bloqueo:${s.bloqueoID}`;
        if (!propPrimerSlot.has(k)) propPrimerSlot.set(k, s);
      }
    });

    const parseT = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
    const fmtT = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

    const movimientos = [];
    const resumenLineas = [];
    origPrimerSlot.forEach((origSlot, key) => {
      const propSlot = propPrimerSlot.get(key);
      if (!propSlot) return;
      if (origSlot.horaInicio === propSlot.horaInicio) return;

      const [tipoStr, id] = key.split(":");
      const reservaIni = origSlot.reservaHoraInicio || origSlot.horaInicio;
      const reservaFin = origSlot.reservaHoraFin || origSlot.horaFin;
      const duracionMin = parseT(reservaFin) - parseT(reservaIni);
      const deltaMin = parseT(origSlot.horaInicio) - parseT(propSlot.horaInicio);
      const nuevaIni = propSlot.horaInicio;
      const nuevaFin = fmtT(parseT(nuevaIni) + duracionMin);

      const signo = deltaMin > 0 ? "−" : "+";
      const absMin = Math.abs(deltaMin);
      if (tipoStr === "reserva") {
        movimientos.push({ tipo: "RESERVA", reservaID: id, horaInicioNueva: nuevaIni, horaFinNueva: nuevaFin });
        const etiqueta = origSlot.jugadores?.[0]?.shortName || origSlot.userFullName || origSlot.userName || "Reserva";
        resumenLineas.push(`• ${etiqueta}: ${reservaIni}hs → ${nuevaIni}hs (${signo}${absMin} min)`);
      } else {
        movimientos.push({ tipo: "BLOQUEO", bloqueoID: id, horaInicioNueva: nuevaIni, horaFinNueva: nuevaFin });
        resumenLineas.push(`• Mantenimiento: ${reservaIni}hs → ${nuevaIni}hs (${signo}${absMin} min)`);
      }
    });

    if (movimientos.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Sin cambios",
        text: "La propuesta es idéntica al estado actual, no hay nada que aplicar.",
      });
      return;
    }

    const confirm = await Swal.fire({
      title: "Aplicar propuesta de optimización",
      html: `
        <div style="text-align: left; font-size: 14px;">
          <p>Se van a realizar los siguientes cambios en <b>${canchaPropuesta.name}</b>:</p>
          <pre style="text-align: left; background: #f8f9fa; padding: 8px; border-radius: 4px; white-space: pre-wrap;">${resumenLineas.join("\n")}</pre>
          <p class="text-muted" style="font-size: 12px;">Se notificará por push a todos los jugadores registrados.</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Aplicar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0d6efd",
    });

    if (!confirm.isConfirmed) return;

    // Formatear fecha YYYY-MM-DD
    const y = fechaSeleccionada.getFullYear();
    const m = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
    const d = String(fechaSeleccionada.getDate()).padStart(2, "0");
    const fechaISO = `${y}-${m}-${d}`;

    try {
      Swal.fire({ title: "Aplicando...", didOpen: () => Swal.showLoading(), allowOutsideClick: false });
      const resp = await aplicarPropuestaOptimizacionAPI({
        activoID: canchaPropuesta._id,
        fecha: fechaISO,
        movimientos,
      });
      Swal.close();
      const data = resp?.data?.data;
      const resumenOk = `Se movieron ${data?.reservasMovidas || 0} reserva(s) y ${data?.bloqueosMovidos || 0} mantenimiento(s).`;
      Swal.fire({
        icon: "success",
        title: "Propuesta aplicada",
        text: `${resumenOk} Se notificó a los jugadores.`,
        confirmButtonColor: "#0d6efd",
      });
      setCanchaPropuesta(null);
      setSlotsPropuesta(null);
    } catch (err) {
      Swal.close();
      const msg = err?.response?.data?.message || err?.message || "Error al aplicar propuesta";
      Swal.fire({ icon: "error", title: "No se pudo aplicar", text: msg });
    }
  }, [canchaPropuesta, slotsPropuesta, slotsMatriz, fechaSeleccionada]);

  // Recalcular propuesta si cambian los slots de la cancha activa (por sockets, etc.)
  useEffect(() => {
    if (!canchaPropuesta) return;
    const slotsOrig = slotsMatriz[canchaPropuesta._id];
    if (!slotsOrig) return;
    const propuesta = calcularPropuestaOptimizacion(slotsOrig, maxSlotsMovimiento);
    const propuestaConColor = asignarColoresCanchaRef.current
      ? asignarColoresCanchaRef.current(propuesta, canchaPropuesta._id)
      : propuesta;
    setSlotsPropuesta(propuestaConColor);
  }, [slotsMatriz, canchaPropuesta, maxSlotsMovimiento]);

  // Suspender reserva pidiendo motivo (solo Admin y Gestor de Reservas)
  const handleSuspenderReserva = async (reservaID) => {
    // Verificar si tiene cargos adicionales
    try {
      const resp = await clienteAxios.get(`/cancha-reservas/${reservaID}`);
      const cargos = resp.data.data?.cargosAdicionales || [];
      if (cargos.length > 0) {
        Swal.fire({ icon: "warning", title: "No se puede suspender", text: "No se puede suspender una reserva con cargos adicionales, elimínelos primero." });
        return;
      }
    } catch (e) { /* continuar si falla la verificación */ }

    const { value: motivo } = await Swal.fire({
      title: "Suspender Reserva",
      text: "Ingresá el motivo de la suspensión:",
      input: "text",
      inputPlaceholder: "Motivo de suspensión...",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmar suspensión",
      confirmButtonColor: "#6c757d",
      cancelButtonText: "Volver",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Debés ingresar un motivo para suspender la reserva.";
        }
      },
    });

    if (motivo) {
      await suspenderReserva(reservaID, motivo.trim());
      cargarSlotsTodasCanchas();
    }
  };

  // Reactivar reserva suspendida (solo Admin y Gestor de Reservas)
  const handleReactivarReserva = async (reservaID) => {
    const result = await Swal.fire({
      title: "¿Reactivar reserva?",
      text: "La reserva volverá a estado CONFIRMADA.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, reactivar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      await reactivarReserva(reservaID);
      cargarSlotsTodasCanchas();
    }
  };

  const handleClickSlot = useCallback(async (cancha, slot) => {
    // CANCHERO: solo visualización. Permitir únicamente ver el detalle de slots BLOQUEADOS.
    if (checkIfCanchero() && slot.estado !== "BLOQUEADO") return;

    const fechaStr = moment(fechaSeleccionada).locale('es').format('dddd D [de] MMMM [de] YYYY');
    const fechaFormateada = fechaStr.charAt(0).toUpperCase() + fechaStr.slice(1);

    // Obtener datos completos de la reserva al hacer click (siempre frescos)
    if ((slot.estado === 'OCUPADO' || slot.estado === 'SUSPENDIDA') && slot.reservaID) {
      try {
        const resp = await clienteAxios.get(`/cancha-reservas/${slot.reservaID}`);
        const reserva = resp.data.data;
        if (reserva) {
          slot = {
            ...slot,
            userFullName: reserva.userFullName || reserva.userName,
            userName: reserva.userName,
            jugadores: reserva.jugadores || [],
            cargosAdicionales: reserva.cargosAdicionales || [],
            presencial: reserva.presencial || false,
            observada: reserva.observada || false,
            observacion: reserva.observacion,
            nota: reserva.nota,
            motivoCancelacion: reserva.motivoCancelacion,
          };
        }
      } catch (err) {
        // Si falla el fetch, mostrar con los datos que tenemos
      }
    }

    if (slot.estado === "DISPONIBLE") {
      // Si es Admin o Gestor, mostrar opción de bloquear
      if (checkIfAdmin() || checkIfGestorReservas()) {
        Swal.fire({
          icon: "question",
          title: "Slot Disponible",
          html: `
            <p><strong>Fecha:</strong> ${fechaFormateada}</p>
            <p><strong>Cancha:</strong> ${cancha.name}</p>
            <p><strong>Horario:</strong> ${slot.horaInicio}</p>
          `,
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: "Reservar",
          denyButtonText: "🔒 Bloquear",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#007bff",
          denyButtonColor: "#6c757d",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            // Proceder con la reserva normal
            const resultado = puedeIniciarReservaEnSlot(cancha._id, slot.horaInicio);
            if (resultado.puede) {
              setCanchaSeleccionada(cancha);
              setSlotSeleccionado(slot);
              setShowModalReserva(true);
            } else {
              const tipoJuegoTexto =
                tipoJuegoSeleccionado === "SINGLES" ? "Singles (60 min)" :
                tipoJuegoSeleccionado === "SINGLES_45" ? "Singles (45 min)" :
                tipoJuegoSeleccionado === "DOBLES_60" ? "Dobles (60 min)" :
                tipoJuegoSeleccionado === "DOBLES" ? "Dobles (75 min)" :
                tipoJuegoSeleccionado === "SINGLES_PADEL" ? "Singles Pádel (90 min)" :
                tipoJuegoSeleccionado === "DOBLES_PADEL" ? "Dobles Pádel (90 min)" :
                "Dobles (90 min)";

              if (resultado.motivo === "HORARIO_PASADO") {
                Swal.fire({
                  icon: "info",
                  title: "Horario no disponible",
                  html: `<p>Este horario ya pasó. Por favor, seleccioná un horario futuro marcado en <strong style="color: #28a745;">verde fuerte</strong>.</p>`,
                  confirmButtonText: "Entendido",
                  confirmButtonColor: "#6c757d",
                });
              } else if (resultado.motivo === "SLOTS_INSUFICIENTES") {
                Swal.fire({
                  icon: "warning",
                  title: "No se puede iniciar aquí",
                  html: `
                    <p>No hay suficientes slots consecutivos disponibles para completar una reserva de <strong>${tipoJuegoTexto}</strong>.</p>
                    <p>Por favor, selecciona otro horario marcado en <strong style="color: #28a745;">verde fuerte</strong>.</p>
                  `,
                });
              } else if (resultado.motivo === "SINGLES_45_REQUIERE_3_SLOTS_ENCERRADOS") {
                Swal.fire({
                  icon: "info",
                  title: "Singles de 45 min tiene reglas especiales",
                  html: `
                    <p><strong>Singles de 45 minutos</strong> solo está disponible en situaciones especiales:</p>
                    <ul style="text-align: left; margin: 15px 40px;">
                      <li>Cuando quedan <strong>exactamente 3 slots consecutivos</strong> (45 minutos)</li>
                      <li>Que estén <strong>completamente encerrados</strong> por:
                        <ul>
                          <li>Otras reservas</li>
                          <li>Bloqueos de cancha</li>
                          <li>Inicio o fin de jornada</li>
                        </ul>
                      </li>
                    </ul>
                    <p>Esto permite aprovechar espacios que de otra forma quedarían vacíos.</p>
                    <p style="margin-top: 10px;"><strong>💡 Nota:</strong> En horario off-peak (después de la hora de fin de pico), esta restricción no aplica y Singles 45 se puede reservar libremente.</p>
                    <p><strong>Para otros horarios, elegí un slot en <span style="color: #28a745;">verde fuerte</span>.</strong></p>
                  `,
                  confirmButtonText: "Entendido",
                  confirmButtonColor: "#6c757d",
                });
              } else if (resultado.motivo === "HUECO_NO_PARTICIONABLE") {
                Swal.fire({
                  icon: "info",
                  title: "Este horario no puede usarse como inicio",
                  html: `
                    <p>Queremos que todos puedan encontrar cancha y evitar huecos difíciles de usar.</p>
                    <p><strong>Elegí un horario en <span style="color: #28a745;">verde fuerte</span> para reservar.</strong></p>
                  `,
                  showCancelButton: true,
                  confirmButtonText: "Entendido",
                  cancelButtonText: "📖 Explicación técnica",
                  confirmButtonColor: "#6c757d",
                  cancelButtonColor: "#17a2b8",
                }).then((result) => {
                  if (result.dismiss === Swal.DismissReason.cancel) {
                    window.open("/explicacion-reservas", "_blank");
                  }
                });
              } else {
                Swal.fire({
                  icon: "warning",
                  title: "No se puede iniciar aquí",
                  html: `
                    <p>No se puede iniciar una reserva de <strong>${tipoJuegoTexto}</strong> en este horario.</p>
                    <p>Por favor, selecciona otro horario marcado en <strong style="color: #28a745;">verde fuerte</strong>.</p>
                  `,
                });
              }
            }
          } else if (result.isDenied) {
            // Bloquear el slot
            crearBloqueoUnitario(cancha, slot);
          }
        });
        return;
      }

      // Validar fecha abierta y presencial para SOCIO (no Admin ni Gestor)
      if (checkIfSocio() && !checkIfAdmin() && !checkIfGestorReservas()) {
        if (!fechaAbierta) {
          Swal.fire({
            icon: "warning",
            title: "Fecha no disponible",
            text: "Esta fecha no está abierta para reservas de socios. Por favor contacta al administrador.",
          });
          return;
        }
        if (fechaPresencial) {
          Swal.fire({
            icon: "warning",
            title: "Reserva presencial",
            text: "Esta fecha es solo para reservas presenciales.",
          });
          return;
        }
      }

      // Verificar si puede iniciar una reserva desde este slot
      const resultado = puedeIniciarReservaEnSlot(cancha._id, slot.horaInicio);

      if (resultado.puede) {
        setCanchaSeleccionada(cancha);
        setSlotSeleccionado(slot);
        setShowModalReserva(true);
      } else {
        const tipoJuegoTexto =
          tipoJuegoSeleccionado === "SINGLES" ? "Singles (60 min)" :
          tipoJuegoSeleccionado === "SINGLES_45" ? "Singles (45 min)" :
          tipoJuegoSeleccionado === "DOBLES_60" ? "Dobles (60 min)" :
          tipoJuegoSeleccionado === "DOBLES" ? "Dobles (75 min)" :
          tipoJuegoSeleccionado === "SINGLES_PADEL" ? "Singles Pádel (90 min)" :
          tipoJuegoSeleccionado === "DOBLES_PADEL" ? "Dobles Pádel (90 min)" :
          "Dobles (90 min)";

        // Determinar el mensaje según el motivo
        if (resultado.motivo === "HORARIO_PASADO") {
          Swal.fire({
            icon: "info",
            title: "Horario no disponible",
            html: `<p>Este horario ya pasó. Por favor, seleccioná un horario futuro marcado en <strong style="color: #28a745;">verde fuerte</strong>.</p>`,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#6c757d",
          });
        } else if (resultado.motivo === "SLOTS_INSUFICIENTES") {
          Swal.fire({
            icon: "warning",
            title: "No se puede iniciar aquí",
            html: `
              <p>No hay suficientes slots consecutivos disponibles para completar una reserva de <strong>${tipoJuegoTexto}</strong>.</p>
              <p>Por favor, selecciona otro horario marcado en <strong style="color: #28a745;">verde fuerte</strong>.</p>
            `,
          });
        } else if (resultado.motivo === "SINGLES_45_REQUIERE_3_SLOTS_ENCERRADOS") {
          Swal.fire({
            icon: "info",
            title: "Singles de 45 min tiene reglas especiales",
            html: `
              <p><strong>Singles de 45 minutos</strong> solo está disponible en situaciones especiales:</p>
              <ul style="text-align: left; margin: 15px 40px;">
                <li>Cuando quedan <strong>exactamente 3 slots consecutivos</strong> (45 minutos)</li>
                <li>Que estén <strong>completamente encerrados</strong> por:
                  <ul>
                    <li>Otras reservas</li>
                    <li>Bloqueos de cancha</li>
                    <li>Inicio o fin de jornada</li>
                  </ul>
                </li>
              </ul>
              <p>Esto permite aprovechar espacios que de otra forma quedarían vacíos.</p>
              <p><strong>Para otros horarios, elegí un slot en <span style="color: #28a745;">verde fuerte</span>.</strong></p>
            `,
            confirmButtonText: "Entendido",
            confirmButtonColor: "#6c757d",
          });
        } else if (resultado.motivo === "HUECO_NO_PARTICIONABLE") {
          Swal.fire({
            icon: "info",
            title: "Este horario no puede usarse como inicio",
            html: `
              <p>Queremos que todos puedan encontrar cancha y evitar huecos difíciles de usar.</p>
              <p><strong>Elegí un horario en <span style="color: #28a745;">verde fuerte</span> para reservar.</strong></p>
            `,
            showCancelButton: true,
            confirmButtonText: "Entendido",
            cancelButtonText: "📖 Explicación técnica",
            confirmButtonColor: "#6c757d",
            cancelButtonColor: "#17a2b8",
          }).then((result) => {
            if (result.dismiss === Swal.DismissReason.cancel) {
              // Abrir explicación técnica en nueva pestaña
              window.open("/explicacion-reservas", "_blank");
            }
          });
        } else {
          Swal.fire({
            icon: "warning",
            title: "No se puede iniciar aquí",
            html: `
              <p>No se puede iniciar una reserva de <strong>${tipoJuegoTexto}</strong> en este horario.</p>
              <p>Por favor, selecciona otro horario marcado en <strong style="color: #28a745;">verde fuerte</strong>.</p>
            `,
          });
        }
      }
    } else if (slot.estado === "SUSPENDIDA") {
      // Formatear lista de jugadores en formato 2x2 con servicios
      let jugadoresHTML = "";
      if (slot.jugadores && slot.jugadores.length > 0) {
        const jugadoresFiltrados = slot.jugadores.filter(j => j.nombre && j.nombre.trim() !== "");
        if (jugadoresFiltrados.length > 0) {
          const formatServicios = (j) => {
            if (!j.servicios || j.servicios.length === 0) return "";
            return j.servicios.map(s => {
              if (s.concepto === "Luz") {
                const totalSec = s.cantidad >= 60 ? s.cantidad : s.cantidad * 3600;
                const h = Math.floor(totalSec / 3600);
                const m = Math.round((totalSec % 3600) / 60);
                const cant = `${h}:${String(m).padStart(2, '0')}`;
                return `<span style="font-size: 0.85em; color: #666; margin-left: 12px;">${s.concepto}: ${cant} hs</span>`;
              } else {
                const cant = String(s.cantidad).replace(".", ",");
                return `<span style="font-size: 0.85em; color: #666; margin-left: 12px;">${s.concepto}: ${cant} unid.</span>`;
              }
            }).join("<br>");
          };
          let filas = "";
          for (let i = 0; i < jugadoresFiltrados.length; i += 2) {
            const jugador1 = jugadoresFiltrados[i];
            const jugador2 = jugadoresFiltrados[i + 1];
            const serv1 = formatServicios(jugador1);
            const serv2 = jugador2 ? formatServicios(jugador2) : "";
            filas += `
              <tr>
                <td style="padding: 5px; text-align: left; vertical-align: top;">• ${jugador1.nombre}${serv1 ? `<br>${serv1}` : ""}</td>
                ${jugador2 ? `<td style="padding: 5px; text-align: left; vertical-align: top;">• ${jugador2.nombre}${serv2 ? `<br>${serv2}` : ""}</td>` : '<td></td>'}
              </tr>
            `;
          }
          jugadoresHTML = `
            <p><strong>Jugadores:</strong></p>
            <table style="margin: 0 auto; width: 80%;">
              ${filas}
            </table>
          `;
        }
      }

      const nombreUsuario = slot.userFullName || slot.userName || "Usuario";
      const puedeEliminar = checkIfAdmin() || checkIfGestorReservas();

      // Armar HTML de cargos adicionales si existen
      let cargosHTMLSusp = "";
      if (slot.cargosAdicionales && slot.cargosAdicionales.length > 0) {
        const items = slot.cargosAdicionales.map(c => {
          if (c.concepto === "Luz") {
            const totalSec = c.cantidad >= 60 ? c.cantidad : c.cantidad * 3600;
            const h = Math.floor(totalSec / 3600);
            const m = Math.round((totalSec % 3600) / 60);
            const cantidadDisplay = `${h}:${String(m).padStart(2, '0')}`;
            return `<span style="margin: 0 10px;">${c.concepto}: <strong>${cantidadDisplay} hs</strong></span>`;
          } else {
            const cantidadDisplay = String(c.cantidad).replace(".", ",");
            return `<span style="margin: 0 10px;">${c.concepto}: <strong>${cantidadDisplay} unid.</strong></span>`;
          }
        }).join("");
        cargosHTMLSusp = `<p style="margin-top: 15px;"><strong>Cargos adicionales:</strong></p><div style="display: flex; justify-content: center;">${items}</div>`;
      }

      const opciones = {
        icon: "warning",
        title: "Reserva Suspendida",
        html: `
          <p><strong>Fecha:</strong> ${fechaFormateada}</p>
          <p><strong>Cancha:</strong> ${cancha.name}</p>
          <p><strong>Horario:</strong> ${slot.reservaHoraInicio || slot.horaInicio} a ${slot.reservaHoraFin || slot.horaFin}</p>
          <p><strong>Reservado por:</strong> ${nombreUsuario}</p>
          <p><strong>Tipo:</strong> ${slot.tipoJuego}</p>
          ${jugadoresHTML}
          ${cargosHTMLSusp}
          ${slot.motivoCancelacion ? `<p><strong>Motivo de suspensión:</strong> ${slot.motivoCancelacion}</p>` : ''}
        `,
        showCloseButton: true,
        showConfirmButton: false,
      };

      if (puedeEliminar && slot.reservaID) {
        opciones.showConfirmButton = true;
        opciones.confirmButtonText = "✅ Reactivar Reserva";
        opciones.confirmButtonColor = "#28a745";
        opciones.showDenyButton = true;
        opciones.denyButtonText = "🗑️ Eliminar";
        opciones.denyButtonColor = "#dc3545";
      }

      Swal.fire(opciones).then((result) => {
        if (result.isConfirmed && slot.reservaID) {
          handleReactivarReserva(slot.reservaID);
        } else if (result.isDenied && slot.reservaID) {
          handleEliminarReserva(slot.reservaID);
        }
      });
    } else if (slot.estado === "OCUPADO") {
      // Formatear lista de jugadores en formato 2x2 con servicios
      let jugadoresHTML = "";
      if (slot.jugadores && slot.jugadores.length > 0) {
        const jugadoresFiltrados = slot.jugadores.filter(j => j.nombre && j.nombre.trim() !== "");
        if (jugadoresFiltrados.length > 0) {
          const formatServicios = (j) => {
            if (!j.servicios || j.servicios.length === 0) return "";
            return j.servicios.map(s => {
              if (s.concepto === "Luz") {
                const totalSec = s.cantidad >= 60 ? s.cantidad : s.cantidad * 3600;
                const h = Math.floor(totalSec / 3600);
                const m = Math.round((totalSec % 3600) / 60);
                const cant = `${h}:${String(m).padStart(2, '0')}`;
                return `<span style="font-size: 0.85em; color: #666; margin-left: 12px;">${s.concepto}: ${cant} hs</span>`;
              } else {
                const cant = String(s.cantidad).replace(".", ",");
                return `<span style="font-size: 0.85em; color: #666; margin-left: 12px;">${s.concepto}: ${cant} unid.</span>`;
              }
            }).join("<br>");
          };
          let filas = "";
          for (let i = 0; i < jugadoresFiltrados.length; i += 2) {
            const jugador1 = jugadoresFiltrados[i];
            const jugador2 = jugadoresFiltrados[i + 1];
            const serv1 = formatServicios(jugador1);
            const serv2 = jugador2 ? formatServicios(jugador2) : "";

            filas += `
              <tr>
                <td style="padding: 5px; text-align: left; vertical-align: top;">• ${jugador1.nombre}${serv1 ? `<br>${serv1}` : ""}</td>
                ${jugador2 ? `<td style="padding: 5px; text-align: left; vertical-align: top;">• ${jugador2.nombre}${serv2 ? `<br>${serv2}` : ""}</td>` : '<td></td>'}
              </tr>
            `;
          }

          jugadoresHTML = `
            <p><strong>Jugadores:</strong></p>
            <table style="margin: 0 auto; width: 80%;">
              ${filas}
            </table>
          `;
        }
      }

      // Solo admin/gestor puede editar reservas, suspender, eliminar o gestionar notas/cargos
      const esAdmin = checkIfAdmin() || checkIfGestorReservas();
      const puedeEditar = esAdmin;
      const puedeEliminar = esAdmin;

      // Un Socio puede cancelar si es dueño o jugador de la reserva, y solo si faltan
      // 6 horas o más para el inicio del turno (misma regla que valida el backend)
      const esParteDeLaReserva = slot.userName === usuario?.userName ||
        (slot.jugadores || []).some(j => j.userNameJ === usuario?.userName);
      const horaInicioReserva = slot.reservaHoraInicio || slot.horaInicio;
      const [hIniReserva, mIniReserva] = horaInicioReserva.split(':').map(Number);
      const inicioReserva = new Date(fechaSeleccionada.getFullYear(), fechaSeleccionada.getMonth(), fechaSeleccionada.getDate(), hIniReserva, mIniReserva);
      const faltanAlMenos6Horas = (inicioReserva.getTime() - Date.now()) >= 6 * 60 * 60 * 1000;
      const puedeCancelarSocio = !esAdmin && esParteDeLaReserva && faltanAlMenos6Horas;

      const nombreUsuario = slot.userFullName || slot.userName || "Usuario";
      const nombreConPresencial = slot.presencial ? `${nombreUsuario} (Presencial)` : nombreUsuario;

      // Armar HTML de cargos adicionales si existen
      let cargosHTML = "";
      if (slot.cargosAdicionales && slot.cargosAdicionales.length > 0) {
        const items = slot.cargosAdicionales.map(c => {
          if (c.concepto === "Luz") {
            const totalSec = c.cantidad >= 60 ? c.cantidad : c.cantidad * 3600;
            const h = Math.floor(totalSec / 3600);
            const m = Math.round((totalSec % 3600) / 60);
            const cantidadDisplay = `${h}:${String(m).padStart(2, '0')}`;
            return `<span style="margin: 0 10px;">${c.concepto}: <strong>${cantidadDisplay} hs</strong></span>`;
          } else {
            const cantidadDisplay = String(c.cantidad).replace(".", ",");
            return `<span style="margin: 0 10px;">${c.concepto}: <strong>${cantidadDisplay} unid.</strong></span>`;
          }
        }).join("");
        cargosHTML = `<p style="margin-top: 15px;"><strong>Cargos adicionales:</strong></p><div style="display: flex; justify-content: center;">${items}</div>`;
      }

      // Versión texto-plano para copiar al portapapeles
      let jugadoresPlano = "";
      if (slot.jugadores && slot.jugadores.length > 0) {
        const filtrados = slot.jugadores.filter(j => j.nombre && j.nombre.trim() !== "");
        if (filtrados.length > 0) {
          jugadoresPlano = filtrados.map(j => {
            let linea = `• ${j.nombre}`;
            if (j.servicios && j.servicios.length > 0) {
              const servs = j.servicios.map(s => {
                if (s.concepto === "Luz") {
                  const totalSec = s.cantidad >= 60 ? s.cantidad : s.cantidad * 3600;
                  const h = Math.floor(totalSec / 3600);
                  const m = Math.round((totalSec % 3600) / 60);
                  return `   ${s.concepto}: ${h}:${String(m).padStart(2, '0')} hs`;
                }
                const cant = String(s.cantidad).replace(".", ",");
                return `   ${s.concepto}: ${cant} unid.`;
              }).join("\n");
              linea += `\n${servs}`;
            }
            return linea;
          }).join("\n");
        }
      }
      let cargosPlano = "";
      if (slot.cargosAdicionales && slot.cargosAdicionales.length > 0) {
        cargosPlano = slot.cargosAdicionales.map(c => {
          if (c.concepto === "Luz") {
            const totalSec = c.cantidad >= 60 ? c.cantidad : c.cantidad * 3600;
            const h = Math.floor(totalSec / 3600);
            const m = Math.round((totalSec % 3600) / 60);
            return `${c.concepto}: ${h}:${String(m).padStart(2, '0')} hs`;
          }
          const cant = String(c.cantidad).replace(".", ",");
          return `${c.concepto}: ${cant} unid.`;
        }).join("  |  ");
      }
      const textoCopiar =
        `Fecha: ${fechaFormateada}\n` +
        `Cancha: ${cancha.name}\n` +
        `Horario: ${slot.reservaHoraInicio || slot.horaInicio} a ${slot.reservaHoraFin || slot.horaFin}\n` +
        `Reservado por: ${nombreConPresencial}\n` +
        `Tipo: ${slot.tipoJuego || 'No especificado'}` +
        (jugadoresPlano ? `\n\nJugadores:\n${jugadoresPlano}` : "") +
        (cargosPlano ? `\n\nCargos adicionales:\n${cargosPlano}` : "");

      const btnCopiarHTML = `<button id="swal-copiar-btn" class="swal2-styled" style="background-color: #10b981; border: 0; color: white; padding: 10px 24px; border-radius: 5px; cursor: pointer; font-size: 1em; margin-left: 8px;">📋 Copiar</button>`;

      const attachCopiarListener = () => {
        const btn = document.getElementById("swal-copiar-btn");
        if (!btn) return;
        btn.addEventListener("click", async () => {
          try {
            await navigator.clipboard.writeText(textoCopiar);
            Swal.fire({ icon: "success", title: "Copiado", timer: 1200, showConfirmButton: false, toast: true, position: "top-end" });
          } catch (e) {
            console.error("No se pudo copiar al portapapeles", e);
          }
        });
      };

      const opciones = {
        icon: "info",
        title: "Slot Ocupado",
        html: `
          <p><strong>Fecha:</strong> ${fechaFormateada}</p>
          <p><strong>Cancha:</strong> ${cancha.name}</p>
          <p><strong>Horario:</strong> ${slot.reservaHoraInicio || slot.horaInicio} a ${slot.reservaHoraFin || slot.horaFin}</p>
          <p><strong>Reservado por:</strong> ${nombreConPresencial}</p>
          <p><strong>Tipo:</strong> ${slot.tipoJuego}</p>
          ${jugadoresHTML}
          ${cargosHTML}
        `,
        showCloseButton: true,
        footer: btnCopiarHTML,
        didOpen: () => { attachCopiarListener(); },
      };

      // Agregar botón de observaciones para todos si la reserva está observada
      if (slot.observada && !puedeEliminar) {
        opciones.footer = `<button id="swal-observacion-btn" class="swal2-styled" style="background-color: #f59e0b; border: 0; color: white; padding: 10px 24px; border-radius: 5px; cursor: pointer; font-size: 1em;">👁️ Observaciones</button>${btnCopiarHTML}`;
        opciones.didOpen = () => {
          attachCopiarListener();
          const btnObservacion = document.getElementById("swal-observacion-btn");
          if (btnObservacion) {
            btnObservacion.addEventListener("click", () => {
              Swal.fire({
                title: "Observaciones",
                html: `<p style="white-space:pre-line; text-align:left;">${slot.observacion || 'Sin detalle'}</p>`,
                icon: "info",
                confirmButtonText: "Cerrar",
              });
            });
          }
        };
      }

      // Agregar botones según permisos
      if (slot.reservaID) {
        if (puedeEditar) {
          opciones.confirmButtonText = "✏️ Editar";
          opciones.confirmButtonColor = "#007bff";
        } else {
          opciones.showConfirmButton = false;
        }
        if (puedeEliminar) {
          opciones.showDenyButton = true;
          opciones.denyButtonText = "❌ Cancelar Reserva";
          opciones.denyButtonColor = "#e67e22";
          opciones.showCancelButton = true;
          opciones.cancelButtonText = "🗑️ Eliminar";
          opciones.cancelButtonColor = "#dc3545";
          // Agregar botones de Suspender y Registrar Cargos en el footer
          opciones.footer = `
            <button id="swal-suspender-btn" class="swal2-styled" style="background-color: #6c757d; border: 0; color: white; padding: 10px 24px; border-radius: 5px; cursor: pointer; font-size: 1em;">⏸️ Suspender</button>
            <button id="swal-cargos-btn" class="swal2-styled" style="background-color: #17a2b8; border: 0; color: white; padding: 10px 24px; border-radius: 5px; cursor: pointer; font-size: 1em; margin-left: 8px;">💲 Cargos</button>
            ${slot.observada ? `<button id="swal-observacion-btn" class="swal2-styled" style="background-color: #f59e0b; border: 0; color: white; padding: 10px 24px; border-radius: 5px; cursor: pointer; font-size: 1em; margin-left: 8px;">👁️ Observaciones</button>` : ''}
            <button id="swal-nota-btn" class="swal2-styled" style="background-color: #6366f1; border: 0; color: white; padding: 10px 24px; border-radius: 5px; cursor: pointer; font-size: 1em; margin-left: 8px;">📝 ${slot.nota ? 'Editar Nota' : 'Agregar Nota'}</button>
            ${btnCopiarHTML}
          `;
          opciones.didOpen = () => {
            attachCopiarListener();
            const btnSuspender = document.getElementById("swal-suspender-btn");
            if (btnSuspender) {
              btnSuspender.addEventListener("click", () => {
                Swal.close();
                handleSuspenderReserva(slot.reservaID);
              });
            }
            const btnCargos = document.getElementById("swal-cargos-btn");
            if (btnCargos) {
              btnCargos.addEventListener("click", () => {
                Swal.close();
                handleRegistrarCargos(slot.reservaID);
              });
            }
            const btnObservacion = document.getElementById("swal-observacion-btn");
            if (btnObservacion) {
              btnObservacion.addEventListener("click", () => {
                Swal.fire({
                  title: "Observaciones",
                  html: `<p style="white-space:pre-line; text-align:left;">${slot.observacion || 'Sin detalle'}</p>`,
                  icon: "info",
                  confirmButtonText: "Cerrar",
                });
              });
            }
            const btnNota = document.getElementById("swal-nota-btn");
            if (btnNota) {
              btnNota.addEventListener("click", () => {
                Swal.fire({
                  title: slot.nota ? "Editar Nota" : "Agregar Nota",
                  input: "textarea",
                  inputValue: slot.nota || "",
                  inputPlaceholder: "Escribir nota...",
                  showCancelButton: true,
                  confirmButtonText: "Guardar",
                  cancelButtonText: "Cancelar",
                  confirmButtonColor: "#6366f1",
                  inputValidator: () => null,
                }).then(async (result) => {
                  if (result.isConfirmed) {
                    try {
                      const token = localStorage.getItem("token");
                      await clienteAxios.put(
                        `/cancha-reservas/${slot.reservaID}`,
                        { nota: result.value || "" },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      Swal.fire({ title: "Nota guardada", icon: "success", timer: 1500, showConfirmButton: false });
                      cargarSlotsTodasCanchas();
                    } catch (err) {
                      console.error("Error guardando nota:", err);
                      Swal.fire({ title: "Error", text: "No se pudo guardar la nota", icon: "error" });
                    }
                  }
                });
              });
            }
          };
        } else if (puedeCancelarSocio) {
          // Socio parte de la reserva: solo puede cancelar (footer con Copiar/Observaciones
          // ya quedó armado más arriba, sin los botones de gestión de Admin/Gestor)
          opciones.showDenyButton = true;
          opciones.denyButtonText = "❌ Cancelar Reserva";
          opciones.denyButtonColor = "#e67e22";
        }
      } else {
        opciones.showConfirmButton = false;
      }

      Swal.fire(opciones).then((result) => {
        if (result.isConfirmed && slot.reservaID) {
          // Editar reserva
          handleEditarReserva(slot, cancha);
        } else if (result.isDenied && slot.reservaID) {
          // Cancelar reserva (pedir motivo)
          handleCancelarReserva(slot.reservaID);
        } else if (result.dismiss === Swal.DismissReason.cancel && slot.reservaID) {
          // Eliminar reserva (hard delete)
          handleEliminarReserva(slot.reservaID);
        }
      });
    } else if (slot.estado === "BLOQUEADO") {
      // Mostrar información completa del bloqueo para todos los usuarios
      const opcionesBase = {
        icon: "warning",
        title: "Slot Bloqueado",
        html: `
          <div style="text-align: left;">
            <p><strong>Fecha:</strong> ${fechaFormateada}</p>
            <p><strong>Cancha:</strong> ${cancha.name}</p>
            <p><strong>Horario:</strong> ${slot.horaInicio}</p>
            <p><strong>Motivo:</strong> ${slot.tipo || "No especificado"}</p>
            <p><strong>Descripción:</strong> ${slot.descripcion || "Esta cancha está bloqueada en este horario"}</p>
          </div>
        `,
        confirmButtonText: "Cerrar",
        confirmButtonColor: "#6c757d",
      };

      // Si es Admin o Gestor, agregar opción de desbloquear
      if (checkIfAdmin() || checkIfGestorReservas()) {
        opcionesBase.showCancelButton = true;
        opcionesBase.showDenyButton = true;
        opcionesBase.denyButtonText = "🔓 Desbloquear";
        opcionesBase.cancelButtonText = "Cancelar";
        opcionesBase.denyButtonColor = "#28a745";
        opcionesBase.cancelButtonColor = "#6c757d";

        Swal.fire(opcionesBase).then((result) => {
          if (result.isDenied) {
            // Desbloquear el slot
            eliminarBloqueoUnitario(slot);
          }
        });
      } else {
        // Para usuarios Socio, solo mostrar la información sin opción de desbloquear
        Swal.fire(opcionesBase);
      }
    }
  }, [puedeIniciarReservaEnSlot, tipoJuegoSeleccionado, fechaSeleccionada, fechaAbierta, checkIfSocio, checkIfCanchero, checkIfAdmin, checkIfGestorReservas, crearBloqueoUnitario, eliminarBloqueoUnitario, handleSuspenderReserva, handleReactivarReserva]);

  const handleReservaCreada = () => {
    console.log("🔄 Recargando slots después de crear reserva...");
    setShowModalReserva(false);
    setSlotSeleccionado(null);
    setCanchaSeleccionada(null);
    // Recargar slots
    cargarSlotsTodasCanchas();
  };

  const handleBloqueoCreado = useCallback(() => {
    console.log("🔄 Recargando slots después de gestionar bloqueos...");
    // Recargar slots para reflejar los cambios en bloqueos
    cargarSlotsTodasCanchas();
  }, [cargarSlotsTodasCanchas]);

  // Registrar cargos adicionales (solo Admin/Gestor)
  const handleRegistrarCargos = async (reservaID) => {
    // Obtener datos actuales de la reserva
    let jugadores = [];
    try {
      const resp = await clienteAxios.get(`/cancha-reservas/${reservaID}`);
      const reserva = resp.data.data;
      if (reserva?.estado === "SUSPENDIDA") {
        Swal.fire({ icon: "warning", title: "No se pueden aplicar cargos", text: "No se pueden aplicar cargos extra a una reserva suspendida." });
        return;
      }
      jugadores = reserva?.jugadores || [];
    } catch (e) { /* si falla, arrancar vacío */ }

    // Inicializar servicios por jugador (copia profunda)
    const jugadoresData = jugadores.map((j, idx) => ({
      idx,
      nombre: j.nombre || "",
      userNameJ: j.userNameJ || "",
      servicios: (j.servicios || []).map(s => ({ ...s })),
    }));
    const jugadoresConNombre = jugadoresData.filter(j => j.nombre.trim() !== "");
    const ES_INVITADO_PATTERN = /^Invitado\d+$/i;

    // Helper: formatear Luz (segundos → H:MM)
    const formatLuz = (cantidad) => {
      const totalSec = cantidad >= 60 ? cantidad : cantidad * 3600;
      const h = Math.floor(totalSec / 3600);
      const m = Math.round((totalSec % 3600) / 60);
      return `${h}:${String(m).padStart(2, '0')} hs`;
    };

    // Render inputs de cantidad según concepto
    const renderCantidadInputs = (concepto) => {
      if (concepto === "Luz") {
        return `
          <div style="display: flex; gap: 5px;">
            <div style="flex: 1;">
              <label style="font-size: 0.8em; color: #666; display: block;">Hs</label>
              <input id="swal-cant-horas" type="number" min="0" step="1" class="swal2-input" style="margin: 0; width: 100%;" value="0">
            </div>
            <div style="flex: 1;">
              <label style="font-size: 0.8em; color: #666; display: block;">Min</label>
              <select id="swal-cant-minutos" class="swal2-input" style="margin: 0; width: 100%; cursor: pointer;">
                <option value="0">0</option>
                <option value="15">15</option>
                <option value="30">30</option>
                <option value="45">45</option>
              </select>
            </div>
          </div>`;
      }
      return `<input id="swal-cant-cantidad" type="number" min="0" step="1" class="swal2-input" style="margin: 0; width: 100%;" value="0" placeholder="Cantidad">`;
    };

    // Render secciones de jugadores con sus servicios
    const renderJugadorSecciones = () => {
      return jugadoresConNombre.map(j => {
        const serviciosHTML = j.servicios.length > 0
          ? j.servicios.map((s, sIdx) => {
              const cantDisplay = s.concepto === "Luz" ? formatLuz(s.cantidad) : `${s.cantidad} unid.`;
              return `
                <div style="display: flex; justify-content: space-between; align-items: center; padding: 4px 8px; background: #f8f9fa; border-radius: 4px; margin-bottom: 4px;">
                  <span>${s.concepto}: <strong>${cantDisplay}</strong></span>
                  <button class="swal-del-srv" data-j="${j.idx}" data-s="${sIdx}" style="background: #dc3545; color: white; border: none; border-radius: 50%; width: 22px; height: 22px; cursor: pointer; font-size: 13px; line-height: 1; display: flex; align-items: center; justify-content: center;">×</button>
                </div>`;
            }).join("")
          : '<span style="color: #aaa; font-size: 0.85em;">Sin cargos</span>';
        return `
          <div style="padding: 8px 10px; border: 1px solid #dee2e6; border-radius: 6px; margin-bottom: 8px;">
            <div style="font-weight: bold; margin-bottom: 5px; color: #495057;">${j.nombre}</div>
            ${serviciosHTML}
          </div>`;
      }).join("");
    };

    // Opciones de "Imputa a" (excluye invitados Invitado####)
    const opcionesImputa = jugadoresConNombre
      .filter(j => !ES_INVITADO_PATTERN.test(j.userNameJ || ""))
      .map(j => `<option value="${j.idx}">${j.nombre}</option>`)
      .join("");

    const { value: result } = await Swal.fire({
      title: "Registrar Cargos",
      width: 650,
      html: `
        <div style="text-align: left; padding: 0 10px;">
          <div style="display: flex; gap: 8px; align-items: flex-end; margin-bottom: 15px; padding-bottom: 15px; border-bottom: 2px solid #dee2e6;">
            <div style="flex: 2;">
              <label style="font-weight: bold; font-size: 0.85em; display: block; margin-bottom: 3px;">Concepto</label>
              <select id="swal-concepto" class="swal2-input" style="margin: 0; width: 100%; cursor: pointer;">
                <option value="Luz">Luz</option>
                <option value="Tubo de pelotas">Tub. Pelotas</option>
              </select>
            </div>
            <div style="flex: 2;" id="swal-cantidad-wrap">
              <label style="font-weight: bold; font-size: 0.85em; display: block; margin-bottom: 3px;">Cantidad</label>
              ${renderCantidadInputs("Luz")}
            </div>
            <div style="flex: 2;">
              <label style="font-weight: bold; font-size: 0.85em; display: block; margin-bottom: 3px;">Imputa a</label>
              <select id="swal-imputa" class="swal2-input" style="margin: 0; width: 100%; cursor: pointer;">
                <option value="">--</option>
                <option value="reserva">Reserva</option>
                ${opcionesImputa}
              </select>
            </div>
            <div style="flex: 0 0 auto;">
              <button id="swal-agregar-btn" style="background: #28a745; color: white; border: none; border-radius: 4px; padding: 8px 14px; cursor: pointer; font-size: 1.2em; height: 40px; line-height: 1;">+</button>
            </div>
          </div>
          <div id="swal-jugadores-container">
            ${renderJugadorSecciones()}
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      confirmButtonColor: "#28a745",
      cancelButtonText: "Cancelar",
      cancelButtonColor: "#6c757d",
      didOpen: () => {
        // Cambiar inputs de cantidad al cambiar concepto
        document.getElementById("swal-concepto").addEventListener("change", (e) => {
          document.getElementById("swal-cantidad-wrap").innerHTML = `
            <label style="font-weight: bold; font-size: 0.85em; display: block; margin-bottom: 3px;">Cantidad</label>
            ${renderCantidadInputs(e.target.value)}
          `;
        });

        // Refrescar secciones de jugadores y re-attachar listeners de borrado
        const refreshJugadores = () => {
          document.getElementById("swal-jugadores-container").innerHTML = renderJugadorSecciones();
          document.querySelectorAll(".swal-del-srv").forEach(btn => {
            btn.addEventListener("click", () => {
              const jIdx = parseInt(btn.dataset.j);
              const sIdx = parseInt(btn.dataset.s);
              const jug = jugadoresData.find(j => j.idx === jIdx);
              if (jug) jug.servicios.splice(sIdx, 1);
              refreshJugadores();
            });
          });
        };

        // Attach listeners de borrado iniciales
        document.querySelectorAll(".swal-del-srv").forEach(btn => {
          btn.addEventListener("click", () => {
            const jIdx = parseInt(btn.dataset.j);
            const sIdx = parseInt(btn.dataset.s);
            const jug = jugadoresData.find(j => j.idx === jIdx);
            if (jug) jug.servicios.splice(sIdx, 1);
            refreshJugadores();
          });
        });

        // Botón agregar cargo
        document.getElementById("swal-agregar-btn").addEventListener("click", () => {
          const concepto = document.getElementById("swal-concepto").value;
          const imputaA = document.getElementById("swal-imputa").value;
          if (!imputaA) return;

          let cantidad;
          if (concepto === "Luz") {
            const h = parseInt(document.getElementById("swal-cant-horas")?.value) || 0;
            const m = parseInt(document.getElementById("swal-cant-minutos")?.value) || 0;
            cantidad = (h * 3600) + (m * 60);
          } else {
            cantidad = parseInt(document.getElementById("swal-cant-cantidad")?.value) || 0;
          }
          if (cantidad <= 0) return;

          if (imputaA === "reserva") {
            // Dividir entre los jugadores con nombre, excluyendo invitados (Invitado####).
            // Si no quedan no-invitados, fallback a dividir entre todos.
            const noInvitados = jugadoresConNombre.filter(j => !ES_INVITADO_PATTERN.test(j.userNameJ || ""));
            const destinatarios = noInvitados.length > 0 ? noInvitados : jugadoresConNombre;
            const cantPorJugador = cantidad / destinatarios.length;
            destinatarios.forEach(j => {
              j.servicios.push({ concepto, cantidad: cantPorJugador });
            });
          } else {
            const jug = jugadoresData.find(j => j.idx === parseInt(imputaA));
            if (jug) jug.servicios.push({ concepto, cantidad });
          }

          document.getElementById("swal-imputa").value = "";
          refreshJugadores();
        });
      },
      preConfirm: () => jugadoresData,
    });

    if (result) {
      try {
        // Computar cargosAdicionales como totales agregados
        const totales = {};
        result.forEach(j => {
          j.servicios.forEach(s => {
            totales[s.concepto] = (totales[s.concepto] || 0) + s.cantidad;
          });
        });
        const cargos = Object.entries(totales).map(([concepto, cantidad]) => ({ concepto, cantidad }));

        await clienteAxios.put(`/cancha-reservas/${reservaID}/cargos`, {
          cargosAdicionales: cargos,
          jugadoresServicios: result.map(j => ({ idx: j.idx, servicios: j.servicios })),
        });
        Swal.fire({ icon: "success", title: "Cargos guardados", timer: 1500, showConfirmButton: false });
        cargarSlotsTodasCanchas();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "No se pudieron guardar los cargos." });
      }
    }
  };

  // Cancelar reserva pidiendo motivo (solo Admin y Gestor de Reservas)
  const handleCancelarReserva = async (reservaID) => {
    // Verificar si tiene cargos adicionales
    try {
      const resp = await clienteAxios.get(`/cancha-reservas/${reservaID}`);
      const cargos = resp.data.data?.cargosAdicionales || [];
      if (cargos.length > 0) {
        Swal.fire({ icon: "warning", title: "No se puede cancelar", text: "No se puede cancelar una reserva con cargos adicionales, elimínelos primero." });
        return;
      }
    } catch (e) { /* continuar si falla la verificación */ }

    const { value: motivo } = await Swal.fire({
      title: "Cancelar Reserva",
      text: "Ingresá el motivo de la cancelación:",
      input: "text",
      inputPlaceholder: "Motivo de cancelación...",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirmar cancelación",
      confirmButtonColor: "#e67e22",
      cancelButtonText: "Volver",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Debés ingresar un motivo para cancelar la reserva.";
        }
      },
    });

    if (motivo) {
      await cancelarReserva(reservaID, motivo.trim());
      cargarSlotsTodasCanchas();
    }
  };

  // Eliminar reserva (solo Admin y Gestor de Reservas)
  const handleEliminarReserva = async (reservaID) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción eliminará la reserva permanentemente. No se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      await eliminarReserva(reservaID);
      // Recargar slots para reflejar el cambio
      cargarSlotsTodasCanchas();
    }
  };

  // Editar reserva (solo Admin y Gestor de Reservas)
  const handleEditarReserva = async (slot, cancha) => {
    console.log("📝 Intentando editar reserva, slot:", slot);

    if (!slot.reservaID) {
      console.error("❌ No se encontró el ID de la reserva en el slot:", slot);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró el ID de la reserva. Por favor, recarga la página e intenta nuevamente.",
      });
      return;
    }

    console.log("🔍 Obteniendo reserva con ID:", slot.reservaID);

    try {
      // Obtener la reserva completa del backend
      const token = localStorage.getItem("token");
      const response = await clienteAxios.get(`/cancha-reservas/${slot.reservaID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Reserva obtenida:", response.data);
      const reservaCompleta = response.data.data;

      // Abrir modal de edición con los datos de la reserva
      setReservaAEditar(reservaCompleta);
      setShowModalEditar(true);
    } catch (error) {
      console.error("❌ Error al obtener la reserva:", error);
      console.error("❌ Error response:", error.response);

      const mensajeError = error.response?.data?.message ||
                          error.message ||
                          "No se pudo cargar la información de la reserva";

      Swal.fire({
        icon: "error",
        title: "Error al cargar reserva",
        text: mensajeError,
      });
    }
  };

  const handleReservaActualizada = () => {
    console.log("🔄 Recargando slots después de actualizar reserva...");
    setShowModalEditar(false);
    setReservaAEditar(null);
    // Recargar slots
    cargarSlotsTodasCanchas();
  };

  // Formatear fecha para input type="date"
  const getFechaInputValue = () => {
    if (!fechaSeleccionada) return "";
    const year = fechaSeleccionada.getFullYear();
    const month = String(fechaSeleccionada.getMonth() + 1).padStart(2, "0");
    const day = String(fechaSeleccionada.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Nota: Ya no se restringe la fecha mínima para socios.
  // Los slots pasados se muestran en verde claro con mensaje "Este horario ya pasó".

  // Formatear fecha para mostrar
  const getFechaFormateada = () => {
    if (!fechaSeleccionada) return "";
    const opciones = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
    const fecha = fechaSeleccionada.toLocaleDateString("es-AR", opciones);
    return fecha.charAt(0).toUpperCase() + fecha.slice(1);
  };

  // Calcular combinaciones posibles para un bloque de slots disponibles
  const calcularCombinaciones = useCallback((slots, indexInicio, cantidadSlots, tipoReserva) => {
    const combinaciones = [];

    // Verificar si es un bloque encerrado de 3 slots para Singles 45
    const esEncerrado = (indexInicio === 0 || slots[indexInicio - 1]?.estado !== "DISPONIBLE") &&
                        (indexInicio + cantidadSlots >= slots.length || slots[indexInicio + cantidadSlots]?.estado !== "DISPONIBLE");

    if (cantidadSlots === 3 && esEncerrado && tipoReserva === "CANCHA_TENIS") {
      const horaInicio = slots[indexInicio].horaInicio;
      const [h, m] = horaInicio.split(":").map(Number);
      const totalMin = h * 60 + m + 45;
      const horaFin = `${String(Math.floor(totalMin / 60)).padStart(2, "0")}:${String(totalMin % 60).padStart(2, "0")}`;

      combinaciones.push({
        descripcion: "1× Singles 45min",
        turnos: [{ tipo: "SINGLES_45", inicio: horaInicio, fin: horaFin }]
      });
      return combinaciones;
    }

    if (tipoReserva === "CANCHA_TENIS") {
      // Generar todas las combinaciones de 4s y 5s que caben en el bloque
      const generarCombinacionesTenis = (numSlots) => {
        const combos = [];
        // Intentar diferentes combinaciones de Singles/Dobles60 (4) y Dobles75 (5)
        const max5s = Math.floor(numSlots / 5);
        for (let num5s = max5s; num5s >= 0; num5s--) {
          const restante = numSlots - (num5s * 5);
          const num4s = Math.floor(restante / 4);
          const slotsUsados = num4s * 4 + num5s * 5;
          if (num4s > 0 || num5s > 0) {
            combos.push({ cuatros: num4s, cincos: num5s, sobrante: numSlots - slotsUsados });
          }
        }
        // Eliminar duplicados (misma cantidad de 4s y 5s)
        const vistos = new Set();
        return combos.filter(c => {
          const key = `${c.cuatros}-${c.cincos}`;
          if (vistos.has(key)) return false;
          vistos.add(key);
          return true;
        });
      };

      const combos = generarCombinacionesTenis(cantidadSlots);

      combos.forEach(({ cuatros, cincos, sobrante }) => {
        const turnos = [];
        let minutosAcumulados = 0;
        const [h, m] = slots[indexInicio].horaInicio.split(":").map(Number);
        const minutosBase = h * 60 + m;

        // Agregar Singles/Dobles60 (4 slots = 60min)
        for (let i = 0; i < cuatros; i++) {
          const inicio = minutosBase + minutosAcumulados;
          const fin = inicio + 60;
          turnos.push({
            tipo: "SINGLES/DOBLES_60",
            inicio: `${String(Math.floor(inicio / 60)).padStart(2, "0")}:${String(inicio % 60).padStart(2, "0")}`,
            fin: `${String(Math.floor(fin / 60)).padStart(2, "0")}:${String(fin % 60).padStart(2, "0")}`
          });
          minutosAcumulados += 60;
        }

        // Agregar Dobles75 (5 slots = 75min)
        for (let i = 0; i < cincos; i++) {
          const inicio = minutosBase + minutosAcumulados;
          const fin = inicio + 75;
          turnos.push({
            tipo: "DOBLES_75",
            inicio: `${String(Math.floor(inicio / 60)).padStart(2, "0")}:${String(inicio % 60).padStart(2, "0")}`,
            fin: `${String(Math.floor(fin / 60)).padStart(2, "0")}:${String(fin % 60).padStart(2, "0")}`
          });
          minutosAcumulados += 75;
        }

        const desc = [];
        if (cuatros > 0) desc.push(`${cuatros}× Singles/Dobles60`);
        if (cincos > 0) desc.push(`${cincos}× Dobles75`);
        combinaciones.push({
          descripcion: desc.join(" + "),
          turnos,
          sobrante
        });
      });
    } else if (tipoReserva === "CANCHA_PADEL") {
      // Pádel: cuántos turnos de 6 caben (90 min)
      const numTurnos = Math.floor(cantidadSlots / 6);
      if (numTurnos > 0) {
        const sobrante = cantidadSlots - (numTurnos * 6);
        const turnos = [];
        let minutosAcumulados = 0;
        const [h, m] = slots[indexInicio].horaInicio.split(":").map(Number);
        const minutosBase = h * 60 + m;

        for (let i = 0; i < numTurnos; i++) {
          const inicio = minutosBase + minutosAcumulados;
          const fin = inicio + 90;
          turnos.push({
            tipo: "DOBLES",
            inicio: `${String(Math.floor(inicio / 60)).padStart(2, "0")}:${String(inicio % 60).padStart(2, "0")}`,
            fin: `${String(Math.floor(fin / 60)).padStart(2, "0")}:${String(fin % 60).padStart(2, "0")}`
          });
          minutosAcumulados += 90;
        }

        combinaciones.push({
          descripcion: `${numTurnos}× 90min`,
          turnos,
          sobrante
        });
      }
    }

    return combinaciones;
  }, []);

  // Obtener análisis de bloques libres para una cancha
  const obtenerAnalisisCancha = useCallback((activoID) => {
    const slots = slotsMatriz[activoID];
    if (!slots) return [];

    const cancha = canchasDisponibles.find(c => c._id === activoID);
    const tipoReserva = cancha?.tipoReserva || "CANCHA_TENIS";

    // Calcular minutos actuales para filtrar slots pasados (solo si es hoy)
    const ahora = new Date();
    const fechaSinHora = new Date(fechaSeleccionada.getFullYear(), fechaSeleccionada.getMonth(), fechaSeleccionada.getDate());
    const hoySinHora = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
    const esHoy = fechaSinHora.getTime() === hoySinHora.getTime();
    const esPasado = fechaSinHora < hoySinHora;
    const minutosActuales = esHoy ? ahora.getHours() * 60 + ahora.getMinutes() : null;

    const esSlotPasado = (slot) => {
      if (esPasado) return true;
      if (!esHoy) return false;
      const [h, m] = slot.horaInicio.split(":").map(Number);
      return h * 60 + m < minutosActuales;
    };

    const bloques = [];
    let i = 0;

    while (i < slots.length) {
      if (slots[i].estado === "DISPONIBLE" && !esSlotPasado(slots[i])) {
        // Inicio de bloque libre
        const inicio = i;
        let fin = i;

        // Encontrar fin del bloque
        while (fin < slots.length && slots[fin].estado === "DISPONIBLE" && !esSlotPasado(slots[fin])) {
          fin++;
        }

        const cantidadSlots = fin - inicio;
        let combinaciones = calcularCombinaciones(slots, inicio, cantidadSlots, tipoReserva);

        // Si hay combinaciones eficientes (sin sobrante), ocultar las que tienen sobrante
        const tieneEficientes = combinaciones.some(c => c.sobrante === 0);
        if (tieneEficientes) {
          combinaciones = combinaciones.filter(c => c.sobrante === 0);
        }

        bloques.push({
          horaInicio: slots[inicio].horaInicio,
          horaFin: slots[fin - 1].horaFin,
          cantidadSlots,
          combinaciones
        });

        i = fin;
      } else {
        i++;
      }
    }

    return bloques;
  }, [slotsMatriz, canchasDisponibles, calcularCombinaciones, fechaSeleccionada]);

  // Obtener partidos que se están jugando en un momento dado (default: ahora)
  // horaOverride: string "HH:MM" para forzar una hora de prueba
  const obtenerPartidosEnCurso = useCallback((horaOverride = null) => {
    const ahora = new Date();
    const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
    let horaActualMinutos;
    if (horaOverride) {
      const [h, m] = horaOverride.split(":").map(Number);
      horaActualMinutos = h * 60 + m;
    } else {
      horaActualMinutos = ahora.getHours() * 60 + ahora.getMinutes();
    }

    const partidosEnCurso = [];

    canchasDisponibles.forEach(cancha => {
      const slots = slotsMatriz[cancha._id];
      if (!slots) return;

      slots.forEach(slot => {
        // Solo slots ocupados (con reservas)
        if (slot.estado !== "OCUPADO") return;

        // Los datos de reserva están directamente en el slot (no en slot.reserva)
        // Verificar que la fecha de búsqueda sea hoy (la fecha está en fechaSeleccionada, no en el slot)
        const fechaSeleccionadaSinHora = new Date(fechaSeleccionada.getFullYear(), fechaSeleccionada.getMonth(), fechaSeleccionada.getDate());
        if (fechaSeleccionadaSinHora.getTime() !== hoy.getTime()) return;

        // Calcular minutos de inicio y fin de la reserva (están directamente en el slot)
        const [horaIni, minIni] = slot.reservaHoraInicio.split(":").map(Number);
        const [horaFin, minFin] = slot.reservaHoraFin.split(":").map(Number);
        const reservaInicioMinutos = horaIni * 60 + minIni;
        const reservaFinMinutos = horaFin * 60 + minFin;

        // Verificar si la hora actual está dentro del rango de la reserva
        if (horaActualMinutos >= reservaInicioMinutos && horaActualMinutos < reservaFinMinutos) {
          // Verificar si ya agregamos esta reserva (para evitar duplicados)
          const yaExiste = partidosEnCurso.some(p => p.reservaId === slot.reservaID);
          if (!yaExiste) {
            partidosEnCurso.push({
              reservaId: slot.reservaID,
              cancha: cancha.name,
              codCancha: cancha.codActivo,
              horaInicio: slot.reservaHoraInicio,
              horaFin: slot.reservaHoraFin,
              tipoJuego: slot.tipoJuego,
              jugadores: slot.jugadores || [],
              usuario: slot.userFullName || slot.userName
            });
          }
        }
      });
    });

    // Ordenar por cancha
    return partidosEnCurso.sort((a, b) => a.codCancha.localeCompare(b.codCancha));
  }, [slotsMatriz, canchasDisponibles, fechaSeleccionada]);

  // Handler para click en "Horario" - abre mapa de canchas
  const handleHorarioClick = () => {
    setShowMapaCanchas(true);
  };

  return (
    <Container fluid className="reservas-canchas-container" style={{ overflowX: "scroll" }}>
      <Row className="mb-3">
        <Col>
          <h2 className="page-title">
            <Calendar size={32} className="me-2" />
            Reservas Tenis y Pádel
          </h2>
        </Col>
      </Row>

      {/* Pestañas de navegación */}
      <Nav variant="tabs" className="mb-2" activeKey={vistaActual}>
        {!checkIfRevisorReservas() && (
          <Nav.Item>
            <Nav.Link
              eventKey="disponibilidad"
              onClick={() => setVistaActual("disponibilidad")}
            >
              Disponibilidad
            </Nav.Link>
          </Nav.Item>
        )}
        {!checkIfCanchero() && (
          <Nav.Item>
            <Nav.Link
              eventKey="misReservas"
              onClick={() => setVistaActual("misReservas")}
            >
              Mis Reservas
            </Nav.Link>
          </Nav.Item>
        )}
      </Nav>

      {/* Vista de Disponibilidad */}
      {vistaActual === "disponibilidad" && (
        <div style={{ display: "inline-block", minWidth: "100%" }}>
      {/* Controles superiores */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-start">
            {/* Selector de Fecha */}
            <Col md={3}>
              <Form.Group>
                <Form.Label>
                  <strong>Fecha</strong>
                </Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Button variant="outline-secondary" size="sm" onClick={handleDiaAnterior}>
                    ◀
                  </Button>
                  <Form.Control
                    type="date"
                    value={getFechaInputValue()}
                    onChange={handleCambioFecha}
                    onKeyDown={(e) => e.preventDefault()}
                  />
                  <Button variant="outline-secondary" size="sm" onClick={handleDiaSiguiente}>
                    ▶
                  </Button>
                  <Button variant="outline-primary" size="sm" onClick={() => setFechaSeleccionada(new Date())}>
                    Ir a Hoy
                  </Button>
                </div>
                {/* Checkbox Fecha Abierta - Solo visible para Admin/Gestor */}
                {(checkIfAdmin() || checkIfGestorReservas()) && (
                  <div className="mt-2">
                    <Form.Check
                      type="checkbox"
                      id="fecha-abierta-checkbox"
                      label="Fecha Abierta"
                      checked={fechaAbierta}
                      onChange={toggleFechaAbierta}
                      disabled={loadingFechaAbierta}
                    />
                    {fechaAbierta && (
                      <div className="d-flex align-items-center gap-3" style={{marginLeft: '1.5rem', marginTop: '4px'}}>
                        <Form.Check
                          type="checkbox"
                          id="fecha-presencial-checkbox"
                          label="Presencial"
                          className="mb-0"
                          checked={fechaPresencial}
                          onChange={togglePresencial}
                          disabled={loadingFechaAbierta}
                        />
                        <div className="d-flex align-items-center gap-2">
                          <Form.Label className="mb-0" style={{fontSize: '0.85rem'}}>Fin Hora Pico:</Form.Label>
                          <Form.Control
                            type="time"
                            size="sm"
                            style={{width: '120px'}}
                            value={horaFinPicoPorFecha ?? globalSettings?.horaFinPico ?? "20:00"}
                            onChange={(e) => handleChangeHoraFinPico(e.target.value)}
                            onKeyDown={(e) => e.preventDefault()}
                            disabled={loadingFechaAbierta}
                          />
                          {horaFinPicoPorFecha && (
                            <small className="text-muted">(personalizado)</small>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>
            </Col>

            {/* Selector de Tipo de Reserva y Tipo de Juego */}
            <Col md={6} className="text-center">
              <Form.Group>
                <Form.Label>
                  <strong>Tipo de Reserva</strong>
                </Form.Label>
                <div className="d-flex gap-3 justify-content-center">
                  <Form.Check
                    type="radio"
                    label="Tenis"
                    name="tipoReserva"
                    id="tenis-selector"
                    value="CANCHA_TENIS"
                    checked={tipoReservaSeleccionado === "CANCHA_TENIS"}
                    onChange={(e) => setTipoReservaSeleccionado(e.target.value)}
                  />
                  <Form.Check
                    type="radio"
                    label="Pádel"
                    name="tipoReserva"
                    id="padel-selector"
                    value="CANCHA_PADEL"
                    checked={tipoReservaSeleccionado === "CANCHA_PADEL"}
                    onChange={(e) => setTipoReservaSeleccionado(e.target.value)}
                  />
                </div>
              </Form.Group>
              {!checkIfCanchero() && <Form.Group className="mt-2">
                <Form.Label>
                  <strong>Tipo de Juego</strong>
                </Form.Label>
                <div className="d-flex gap-3 justify-content-center">
                  {tipoReservaSeleccionado === "CANCHA_TENIS" && (
                    <>
                      <Form.Check
                        type="radio"
                        label="Singles (60 min)"
                        name="tipoJuego"
                        id="singles-selector"
                        value="SINGLES"
                        checked={tipoJuegoSeleccionado === "SINGLES"}
                        onChange={(e) => setTipoJuegoSeleccionado(e.target.value)}
                      />
                      <Form.Check
                        type="radio"
                        label="Singles (45 min)"
                        name="tipoJuego"
                        id="singles-45-selector"
                        value="SINGLES_45"
                        checked={tipoJuegoSeleccionado === "SINGLES_45"}
                        onChange={(e) => setTipoJuegoSeleccionado(e.target.value)}
                      />
                      <Form.Check
                        type="radio"
                        label="Dobles (75 min)"
                        name="tipoJuego"
                        id="dobles-selector"
                        value="DOBLES"
                        checked={tipoJuegoSeleccionado === "DOBLES"}
                        onChange={(e) => setTipoJuegoSeleccionado(e.target.value)}
                      />
                      <Form.Check
                        type="radio"
                        label="Dobles (60 min)"
                        name="tipoJuego"
                        id="dobles-60-selector"
                        value="DOBLES_60"
                        checked={tipoJuegoSeleccionado === "DOBLES_60"}
                        onChange={(e) => setTipoJuegoSeleccionado(e.target.value)}
                      />
                    </>
                  )}
                  {tipoReservaSeleccionado === "CANCHA_PADEL" && (
                    <>
                      <Form.Check
                        type="radio"
                        label="Singles (90 min)"
                        name="tipoJuego"
                        id="singles-padel-selector"
                        value="SINGLES_PADEL"
                        checked={tipoJuegoSeleccionado === "SINGLES_PADEL"}
                        onChange={(e) => setTipoJuegoSeleccionado(e.target.value)}
                      />
                      <Form.Check
                        type="radio"
                        label="Dobles (90 min)"
                        name="tipoJuego"
                        id="dobles-padel-selector"
                        value="DOBLES_PADEL"
                        checked={tipoJuegoSeleccionado === "DOBLES_PADEL"}
                        onChange={(e) => setTipoJuegoSeleccionado(e.target.value)}
                      />
                    </>
                  )}
                </div>
              </Form.Group>}
            </Col>

            {/* Botones adicionales */}
            <Col md={3} className="text-end">
              <GestorBloqueosButton
                canchas={canchasDisponibles}
                onBloqueoCreado={handleBloqueoCreado}
                puedeGestionar={!checkIfSocio() || checkIfGestorReservas()}
              />
            </Col>
          </Row>

          {/* Info de la fecha seleccionada */}
          <Row className="mt-3">
            <Col>
              <p className="mb-0 text-muted">
                <Calendar size={16} className="me-1" />
                {getFechaFormateada()}
              </p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Leyenda */}
      <Card className="mb-3">
        <Card.Body className="py-2">
          <div className="d-flex gap-3 align-items-center flex-wrap" style={{ rowGap: "0.5rem" }}>
            <button
              type="button"
              className="leyenda-btn"
              onClick={() => setShowModalInicios(true)}
            >
              <span className="leyenda-color" style={{ backgroundColor: "#28a745" }}></span>
              <span>Puede iniciar reserva aquí</span>
            </button>
            <button type="button" className="leyenda-btn">
              <span
                className="leyenda-color"
                style={{ backgroundColor: "#d4edda", border: "1px solid #c3e6cb" }}
              ></span>
              <span>Disponible</span>
            </button>
            <button type="button" className="leyenda-btn">
              <span className="leyenda-color" style={{ backgroundColor: "#dc3545" }}></span>
              <span>Reservado desde otro usuario</span>
            </button>
            <button type="button" className="leyenda-btn">
              <span className="leyenda-color" style={{ backgroundColor: "#6c757d" }}></span>
              <span>Bloqueado</span>
            </button>
            {!checkIfCanchero() && (
              <button type="button" className="leyenda-btn">
                <span className="leyenda-color" style={{ backgroundColor: "#ffc107" }}></span>
                <span>Tu reserva</span>
              </button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Tabla Matriz de Slots */}
      <Card>
        <Card.Body className="p-0">
            {!tipoJuegoSeleccionado ? (
              <div className="text-center py-5 text-muted">
                <Calendar size={48} />
                <p className="mt-3">Por favor, selecciona el tipo de juego (Singles o Dobles) para ver la disponibilidad</p>
              </div>
            ) : loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Cargando disponibilidad...</p>
              </div>
            ) : canchasDisponibles.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <Calendar size={48} />
                <p className="mt-3">No hay canchas reservables configuradas</p>
              </div>
            ) : horariosUnicos.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <Calendar size={48} />
                <p className="mt-3">No hay horarios disponibles para esta fecha</p>
              </div>
            ) : (
              <MatrizSlots
                horariosUnicos={horariosUnicos}
                canchasDisponibles={canchasDisponibles}
                slotsMatriz={slotsMatriz}
                hoverSlot={hoverSlot}
                setHoverSlot={setHoverSlot}
                handleClickSlot={handleClickSlot}
                onCanchaClick={handleCanchaClick}
                onHorarioClick={handleHorarioClick}
                isAdminOrGestor={isAdminOrGestor}
                esHoy={(() => {
                  const hoy = new Date();
                  return fechaSeleccionada.getFullYear() === hoy.getFullYear() &&
                         fechaSeleccionada.getMonth() === hoy.getMonth() &&
                         fechaSeleccionada.getDate() === hoy.getDate();
                })()}
                autoScrollActivo={autoScrollActivo}
                onIniciarRecorrido={iniciarRecorridoWeb}
                onDetenerRecorrido={detenerRecorridoWeb}
                canchaPropuestaID={canchaPropuesta?._id || null}
                slotsPropuesta={slotsPropuesta}
                onPropuestaClick={handlePropuestaOptimizacionClick}
                onCerrarPropuesta={handleCerrarPropuesta}
                onAplicarPropuesta={handleAplicarPropuesta}
              />
            )}
        </Card.Body>
      </Card>

        </div>
      )}

      {/* Vista de Mis Reservas */}
      {vistaActual === "misReservas" && (
        <MisReservas hideTitle={true} />
      )}

      {/* Modal Nueva Reserva */}
      {/* Modal Inicios Disponibles (desde la leyenda "Puede iniciar reserva aquí") */}
      <Modal
        show={showModalInicios}
        onHide={() => setShowModalInicios(false)}
        centered
        dialogClassName="modal-inicios"
        scrollable
      >
        <Modal.Header closeButton style={{ backgroundColor: "#28a745", color: "white" }}>
          <div>
            <Modal.Title style={{ fontSize: "1.1rem" }}>Inicios disponibles</Modal.Title>
            <div style={{ fontSize: "0.85rem", opacity: 0.9 }}>
              {moment(fechaSeleccionada).locale("es").format("dddd DD/MM/YYYY").replace(/^./, (c) => c.toUpperCase())}
              {tipoJuegoSeleccionado ? ` ${tipoJuegoSeleccionado}` : ""}
            </div>
            <div style={{ fontSize: "0.8rem", opacity: 0.8 }}>
              {iniciosOrdenados.length} opcion{iniciosOrdenados.length === 1 ? "" : "es"}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {iniciosOrdenados.length === 0 ? (
            <p className="text-muted fst-italic text-center my-4">
              No hay ningún horario donde se pueda iniciar una reserva de este tipo
            </p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {iniciosOrdenados.map(({ cancha, slot, horaInicio, horaFin }) => (
                <button
                  key={`${cancha._id}-${horaInicio}`}
                  type="button"
                  className="inicio-fila"
                  onClick={() => handleInicioClick(cancha, slot)}
                >
                  <span className="inicio-hora">{horaInicio} - {horaFin}</span>
                  <span className="inicio-cancha">{cancha.name || cancha.codActivo}</span>
                </button>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalInicios(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {showModalReserva && canchaSeleccionada && slotSeleccionado && (
        <ModalNuevaReserva
          show={showModalReserva}
          onHide={() => {
            setShowModalReserva(false);
            setSlotSeleccionado(null);
            setCanchaSeleccionada(null);
          }}
          cancha={canchaSeleccionada}
          fecha={fechaSeleccionada}
          slot={slotSeleccionado}
          tipoJuego={tipoJuegoSeleccionado}
          onReservaCreada={handleReservaCreada}
          fechaPresencial={fechaPresencial}
        />
      )}

      {/* Modal Editar Reserva */}
      {showModalEditar && reservaAEditar && (
        <ModalEditarReserva
          show={showModalEditar}
          onHide={() => {
            setShowModalEditar(false);
            setReservaAEditar(null);
          }}
          reserva={reservaAEditar}
          onReservaActualizada={handleReservaActualizada}
        />
      )}

      {/* Mapa de Canchas */}
      {tipoReservaSeleccionado === "CANCHA_PADEL" ? (
        <MapaCanchasPadel
          show={showMapaCanchas}
          onHide={() => setShowMapaCanchas(false)}
          obtenerPartidosEnCurso={obtenerPartidosEnCurso}
          canchasDisponibles={canchasDisponibles}
          slotsMatriz={slotsMatriz}
        />
      ) : (
        <MapaCanchas
          show={showMapaCanchas}
          onHide={() => setShowMapaCanchas(false)}
          obtenerPartidosEnCurso={obtenerPartidosEnCurso}
          canchasDisponibles={canchasDisponibles}
          slotsMatriz={slotsMatriz}
        />
      )}

      {/* Modal Análisis de Combinaciones */}
      <Modal
        show={showModalAnalisis}
        onHide={() => setShowModalAnalisis(false)}
        size="lg"
        centered
        style={{ zIndex: 9999 }}
        backdropClassName="modal-backdrop-high-z"
      >
        <Modal.Header closeButton style={{ backgroundColor: "#17a2b8", color: "white" }}>
          <Modal.Title>
            📊 Análisis de Combos eficientes - {canchaAnalisis?.name || canchaAnalisis?.codActivo}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <h6 className="mb-3">{canchaAnalisis?.name}</h6>
          {canchaAnalisis && (() => {
            const bloques = obtenerAnalisisCancha(canchaAnalisis._id);
            return bloques.length === 0 ? (
              <p className="text-muted mb-0">
                No hay bloques libres o no se pueden particionar con las reglas actuales
              </p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {bloques.map((bloque, idx) => (
                  <div key={idx} className="border rounded p-3 bg-light">
                    <div className="mb-2">
                      <strong>Bloque Libre:</strong> {bloque.horaInicio} - {bloque.horaFin}
                      <span className="text-muted ms-2">({bloque.cantidadSlots} slots)</span>
                    </div>
                    <div className="ms-3">
                      <strong>Combos posibles:</strong>
                      {bloque.combinaciones.length === 0 ? (
                        <div className="mt-2 text-muted fst-italic">
                          Sin combos posibles ({bloque.cantidadSlots * 15}min no alcanza para ningún tipo de reserva)
                        </div>
                      ) : bloque.combinaciones.map((combo, comboIdx) => (
                        <div key={comboIdx} className="mt-2 border-start border-primary border-3 ps-3">
                          <div className="fw-bold text-primary">
                            {combo.descripcion.split(' + ').map((parte, idx, arr) => (
                              <React.Fragment key={idx}>
                                {parte}
                                {idx < arr.length - 1 && (
                                  <span style={{ fontSize: '1.6em', color: '#004085', fontWeight: 'bold', margin: '0 6px' }}>
                                    +
                                  </span>
                                )}
                              </React.Fragment>
                            ))}
                            {combo.sobrante > 0 && (
                              <span className="text-danger fw-normal ms-2" style={{ fontSize: '0.85em' }}>
                                (sobra {combo.sobrante} slot{combo.sobrante > 1 ? 's' : ''} - {combo.sobrante * 15}min)
                              </span>
                            )}
                          </div>
                          <div className="small row">
                            {combo.turnos.map((turno, turnoIdx) => (
                              <div key={turnoIdx} className="col-6 text-muted">
                                • {turno.tipo}: {turno.inicio} - {turno.fin}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalAnalisis(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

export default ReservasCanchas;
